import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Linkedin, Facebook, Instagram, Music2, Newspaper, Twitter, Eye, UserPlus, BellPlus, Megaphone, MessageCircle } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { API_CONFIG } from '@/services/api';
import { generatePepJob } from '@/services/pep';
import { listInstantlyCampaignOptions, researchContact, type InstantlyCampaignOption } from '@/services/research';
import { getPepCompanyProfile, getPepProfile, savePepProfile, type PepProfile } from '@/services/pepProfile';
import { updateContactRecord } from '@/services/contacts';
import { Header } from '@/components/Header';
import { buildPepSequenceRequestFromResearchMatch } from '@/utils/pepContext';
import linkedinIndustryCodes from '@/data/linkedinIndustryCodes.json';
import { DEPARTMENT_OPTIONS, SENIORITY_OPTIONS } from '@/types/research';
import { PepProfileEditorV2 } from '@/components/PepProfileEditor_v2';

type PepTab = 'profile' | 'linkedin_generation' | 'facebook_generation';
type LinkedinListenerPageTab = 'listener' | 'leads';
type LinkedinLeadGenTab = 'keyword_trend_finder' | 'one_click_lead_gen';
type OneClickChannel = 'linkedin' | 'facebook' | 'instagram' | 'tiktok' | 'substack' | 'x';
type ContactSocialPlatform = 'linkedin' | 'facebook' | 'instagram' | 'tiktok' | 'x';

type OneClickContact = {
  searchResultId?: string;
  contactId?: string;
  id?: string;
  fullName?: string;
  Name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  Title?: string;
  company?: string;
  Company?: string;
  linkedIn?: string;
  LIProfileUrl?: string;
  facebook_url?: string;
  facebookUrl?: string;
  instagram_url?: string;
  instagramUrl?: string;
  tiktok_url?: string;
  tiktokUrl?: string;
  x_url?: string;
  xUrl?: string;
  twitterUrl?: string;
  substack_url?: string;
  substackUrl?: string;
  linkedin_profile_ds?: string[];
  linkedin_posts_ds?: string[];
  [key: string]: unknown;
};

type OneClickLeadRow = {
  channel: OneClickChannel;
  source: 'linkedin' | 'facebook';
  profileUrl: string;
  outcome: LinkedInGenerationOutcome | FacebookGenerationOutcome;
  contact: OneClickContact;
  contactKey: string;
};

type OneClickEngagement = {
  viewedAt?: string;
  connectedAt?: string;
  followedAt?: string;
  commentedAt?: string;
  messagedAt?: string;
};

function getOneClickOutcomeStatus(outcome: LinkedInGenerationOutcome | FacebookGenerationOutcome) {
  if (outcome.researched) return 'researched';
  if (outcome.searchResultId || outcome.contactId) return outcome.error ? 'failed' : 'matched';
  return outcome.error ? 'failed' : 'pending';
}

const GUIDE_CONTEXT_STORAGE_KEY = 'pepai-guide-context-v1';

function safeStorageGet(key: string): string {
  try {
    return window.localStorage.getItem(key) || '';
  } catch (error) {
    console.warn('localStorage get failed', { key, error });
    return '';
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn('localStorage set failed', { key, error });
  }
}

function safeStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn('localStorage remove failed', { key, error });
  }
}

const CHANNEL_LABELS: Record<OneClickChannel, string> = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  substack: 'Substack',
  x: 'X/Twitter',
};

function compactStringList(input: string[]): string[] {
  return input.map((item) => String(item ?? '').trim()).filter(Boolean);
}

function parseMultilineOrCommaList(input: string): string[] {
  return compactStringList(
    String(input ?? '')
      .split(/\n|,/)
      .map((item) => item.trim())
  );
}

function normalizeEngagement(value: unknown): OneClickEngagement {
  const record = asRecord(value);
  return {
    viewedAt: String(record.viewedAt ?? record.viewed_at ?? '').trim() || undefined,
    connectedAt: String(record.connectedAt ?? record.connected_at ?? '').trim() || undefined,
    followedAt: String(record.followedAt ?? record.followed_at ?? '').trim() || undefined,
    commentedAt: String(record.commentedAt ?? record.commented_at ?? '').trim() || undefined,
    messagedAt: String(record.messagedAt ?? record.messaged_at ?? record.dmAt ?? '').trim() || undefined,
  };
}

function getEngagementFromContact(contact: OneClickContact): OneClickEngagement {
  const c = contact as Record<string, unknown>;
  const candidates = [
    c.pepai_engagement,
    c.pepaiEngagement,
    c.oneclick_engagement,
    c.oneClickEngagement,
    c.engagement,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeEngagement(candidate);
    if (normalized.viewedAt || normalized.connectedAt || normalized.followedAt || normalized.commentedAt || normalized.messagedAt) {
      return normalized;
    }
  }
  return {};
}

function formatEngagementTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

type LinkedInCommentProfile = {
  name: string;
  company: string;
  title?: string;
  linkedinUrl: string;
  commentText?: string;
  sourceType?: 'post_author' | 'commenter';
};

type FacebookCommentProfile = {
  name: string;
  company: string;
  facebookUrl: string;
  email?: string;
  commentText?: string;
  sourceType?: 'post_author' | 'commenter';
};

type FacebookProfileDetails = {
  name: string;
  company: string;
  email: string;
  facebookUrl: string;
  website: string;
  about: string;
};

type FacebookSelectedPost = {
  postUrl: string;
  commentCount: number;
  authorFacebookUrl?: string;
  authorName?: string;
  authorCompany?: string;
};

type FacebookGenerationOutcome = {
  profileUrl: string;
  runId?: string;
  name: string;
  company: string;
  triggerPhrase?: string;
  postUrl: string;
  commentText: string;
  matched: boolean;
  researched: boolean;
  searchResultId?: string;
  contactId?: string;
  companyEntityId?: string;
  writeDisposition?: string;
  error?: string;
  searchAttemptType?: string;
};

type FacebookCommentTaskMode = 'specific_post_commenter' | 'group_bulk_comment' | 'group_custom_comment';
type LinkedInCommentTaskMode = 'specific_post_commenter';

type LinkedInAuthorCandidate = LinkedInCommentProfile & {
  postUrl: string;
  commentCount: number;
};

type ScrapedAuthorProfile = LinkedInCommentProfile & {
  location: string;
};

type LinkedInGenerationStats = {
  postsFound: number;
  postsProcessed: number;
  authorProfilesFound: number;
  profilesSearched: number;
  researched: number;
  sequencesQueued: number;
};

type LinkedInSelectedPost = {
  postUrl: string;
  commentCount: number;
  authorLinkedinUrl?: string;
  authorName?: string;
  authorCompany?: string;
  authorTitle?: string;
};

type LinkedInGenerationOutcome = {
  profileUrl: string;
  name: string;
  company: string;
  triggerPhrase?: string;
  postUrl: string;
  commentText: string;
  matched: boolean;
  researched: boolean;
  searchResultId?: string;
  contactId?: string;
  companyEntityId?: string;
  writeDisposition?: string;
  error?: string;
  searchAttemptType?: string;
};

type LinkedInTrendRow = {
  keyword: string;
  postsFound: number;
  runId?: string;
  datasetId?: string;
  postUrls?: string[];
};

type LinkedInIndustryOption = {
  id: number;
  label: string;
  hierarchy: string;
};

const EMPTY_PROFILE: PepProfile = {
  brandVoice: '',
  icpPersona: '',
  offersProducts: '',
  languageDos: '',
  languageDonts: '',
  channelConstraints: '',
  sequencePolicy: '',
  touchpointRules: '',
  approvalMode: 'manual',
};

const emptyStats: LinkedInGenerationStats = {
  postsFound: 0,
  postsProcessed: 0,
  authorProfilesFound: 0,
  profilesSearched: 0,
  researched: 0,
  sequencesQueued: 0,
};

const LINKEDIN_DATE_OPTIONS = [
  { value: '', label: 'Any time', sortType: 'relevance' as const },
  { value: 'past-24h', label: 'Past 24 hours', sortType: 'date_posted' as const },
  { value: 'past-2-weeks', label: 'Past 2 weeks', sortType: 'date_posted' as const },
  { value: 'past-week', label: 'Past week', sortType: 'date_posted' as const },
  { value: 'past-month', label: 'Past month', sortType: 'date_posted' as const },
];

const LINKEDIN_POSTED_LIMIT_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '1hr', label: '1hr' },
  { value: '24hr', label: '24hr' },
  { value: 'week', label: 'week' },
  { value: 'month', label: 'month' },
  { value: '3months', label: '3 months' },
  { value: '6months', label: '6 months' },
  { value: 'year', label: 'year' },
];
const LINKEDIN_SORT_BY_OPTIONS = [
  { value: 'date', label: 'date' },
  { value: 'relevance', label: 'relevance' },
];
const LINKEDIN_CONTENT_TYPE_OPTIONS = [
  { value: 'all', label: 'all' },
  { value: 'videos', label: 'videos' },
  { value: 'images', label: 'images' },
  { value: 'jobs', label: 'jobs' },
  { value: 'live_videos', label: 'live videos' },
  { value: 'documents', label: 'documents' },
  { value: 'collaborative_articles', label: 'collaborative articles' },
];
const LINKEDIN_PROFILE_MODE_OPTIONS = [
  { value: 'short', label: 'short' },
  { value: 'full', label: 'full' },
];

const LINKEDIN_POST_COUNT_OPTIONS = [
  { requested: 5, fetch: 15 },
  { requested: 10, fetch: 25 },
  { requested: 20, fetch: 40 },
  { requested: 25, fetch: 50 },
];
const ONE_CLICK_SCHEDULE_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'] as const;
const ONE_CLICK_SCHEDULE_HOUR = 9;
const LINKEDIN_DEEP_SCRAPE_BATCH_SIZE = 5;
const LINKEDIN_AUTHOR_SCRAPE_BATCH_SIZE = 5;
const PEPAI_CAMPAIGN_STORAGE_KEY = 'profitibull_pepai_campaign_id';
const FACEBOOK_COMMENT_TASK_OPTIONS: Array<{ value: FacebookCommentTaskMode; label: string; help: string }> = [
  {
    value: 'specific_post_commenter',
    label: 'Specific Post Commenter',
    help: 'Create one GoHighLevel task for each discovered Facebook post.',
  },
  {
    value: 'group_bulk_comment',
    label: 'Group Commenter · Bulk',
    help: 'Use one shared comment across every Facebook group URL.',
  },
  {
    value: 'group_custom_comment',
    label: 'Group Commenter · Custom',
    help: 'Provide a separate comment for each Facebook group URL.',
  },
];
const LINKEDIN_COMMENT_TASK_OPTIONS: Array<{ value: LinkedInCommentTaskMode; label: string; help: string }> = [
  {
    value: 'specific_post_commenter',
    label: 'Specific Post Commenter',
    help: 'Create one task for each selected LinkedIn post, with shared or per-post comments.',
  },
];
const COMPANY_PAGE_SENIORITY_OPTIONS = SENIORITY_OPTIONS.filter((value) =>
  ['C-Level', 'VP', 'Director'].includes(value)
);

function asArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is Record<string, unknown> => typeof v === 'object' && v !== null);
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function isLinkedInPostUrl(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v.includes('linkedin.com')) return false;
  return (
    v.includes('/feed/update/') ||
    v.includes('/posts/') ||
    v.includes('/activity-') ||
    v.includes('/ugcpost-') ||
    v.includes('/pulse/')
  );
}

function normalizeLinkedInPostUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();

  // Common LinkedIn URN formats from scrapers/APIs.
  if (lower.startsWith('urn:li:activity:') || lower.startsWith('urn:li:ugcpost:')) {
    return `https://www.linkedin.com/feed/update/${raw}`;
  }

  // Relative LinkedIn post paths.
  if (raw.startsWith('/feed/update/') || raw.startsWith('/posts/')) {
    return `https://www.linkedin.com${raw}`;
  }

  if (lower.startsWith('www.linkedin.com/')) {
    return `https://${raw}`;
  }
  if (lower.startsWith('linkedin.com/')) {
    return `https://www.${raw}`;
  }

  if (/^https?:\/\//i.test(raw)) return raw;
  return raw;
}

function collectLinkedInPostUrls(value: unknown, depth = 0): string[] {
  if (depth > 5 || value == null) return [];
  if (typeof value === 'string') {
    const candidate = normalizeLinkedInPostUrl(value);
    return isLinkedInPostUrl(candidate) ? [candidate] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectLinkedInPostUrls(entry, depth + 1));
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      collectLinkedInPostUrls(entry, depth + 1)
    );
  }
  return [];
}

function isFacebookPostUrl(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v.includes('facebook.com') && !v.includes('fb.watch')) return false;
  return (
    v.includes('/posts/') ||
    v.includes('/permalink.php') ||
    v.includes('/story.php') ||
    v.includes('/photo') ||
    v.includes('/videos/')
  );
}

function normalizeFacebookPostUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();

  if (raw.startsWith('/')) return `https://www.facebook.com${raw}`;
  if (lower.startsWith('www.facebook.com/') || lower.startsWith('m.facebook.com/')) return `https://${raw}`;
  if (lower.startsWith('facebook.com/')) return `https://www.${raw}`;
  if (lower.startsWith('fb.watch/')) return `https://${raw}`;
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw;
}

function collectFacebookPostUrls(value: unknown, depth = 0): string[] {
  if (depth > 5 || value == null) return [];
  if (typeof value === 'string') {
    const candidate = normalizeFacebookPostUrl(value);
    return isFacebookPostUrl(candidate) ? [candidate] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectFacebookPostUrls(entry, depth + 1));
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      collectFacebookPostUrls(entry, depth + 1)
    );
  }
  return [];
}

function getPostUrl(row: Record<string, unknown>): string {
  const data = asRecord(row.data);
  const value =
    row.postUrl ??
    row.post_url ??
    row.postURL ??
    row.postUrlFull ??
    row.url ??
    data.postUrl ??
    data.post_url ??
    data.postURL ??
    data.postUrlFull ??
    data.activityUrl ??
    data.activity_url ??
    data.permalink ??
    data.link ??
    data.url ??
    row.activityUrl ??
    row.activity_url ??
    row.permalink ??
    row.link ??
    row.linkedinPostUrl ??
    row.linkedin_post_url ??
    row.postLink ??
    row.post_link;
  const normalizedValue = normalizeLinkedInPostUrl(value);
  if (isLinkedInPostUrl(normalizedValue)) return normalizedValue;

  // Fallback for URN-only rows.
  const urnCandidate = normalizeLinkedInPostUrl(
    row.postUrn ??
    row.post_urn ??
    row.activityUrn ??
    row.activity_urn ??
    row.urn ??
    data.postUrn ??
    data.post_urn ??
    data.activityUrn ??
    data.activity_urn ??
    data.urn
  );
  if (isLinkedInPostUrl(urnCandidate)) return urnCandidate;

  const discovered = collectLinkedInPostUrls(row);
  return discovered[0] ?? '';
}

function getFacebookPostUrl(row: Record<string, unknown>): string {
  const data = asRecord(row.data);
  const value =
    row.postUrl ??
    row.post_url ??
    row.postURL ??
    row.postUrlFull ??
    row.url ??
    data.postUrl ??
    data.post_url ??
    data.postURL ??
    data.postUrlFull ??
    data.activityUrl ??
    data.activity_url ??
    data.permalink ??
    data.link ??
    data.url ??
    row.activityUrl ??
    row.activity_url ??
    row.permalink ??
    row.link ??
    row.postLink ??
    row.post_link;
  const normalized = normalizeFacebookPostUrl(value);
  if (isFacebookPostUrl(normalized)) return normalized;

  const discovered = collectFacebookPostUrls(row);
  return discovered[0] ?? '';
}

function extractCompanyFromHeadline(headline: string): string {
  const text = headline.trim();
  if (!text) return '';
  const match = text.match(/\bat\s+([^|•·-]{2,120})/i);
  return match?.[1]?.trim() ?? '';
}

function extractLinkedInCommentProfiles(items: Array<Record<string, unknown>>, max = 50): LinkedInCommentProfile[] {
  const out: LinkedInCommentProfile[] = [];
  const seen = new Set<string>();

  const push = (candidate: LinkedInCommentProfile) => {
    const name = candidate.name.trim();
    const linkedinUrl = normalizeLinkedInPersonProfileUrl(candidate.linkedinUrl);
    if (!name || !linkedinUrl) return;
    const key = linkedinUrl.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({
      name,
      company: candidate.company.trim(),
      title: String(candidate.title ?? '').trim(),
      linkedinUrl,
    });
  };

  for (const item of items) {
    const data = asRecord(item.data);
    const commentSources = [
      ...asArray(item.comments),
      ...asArray(item.post_comments),
      ...asArray(item.latest_comments),
      ...asArray(item.top_comments),
      ...asArray(data.comments),
      ...asArray(data.post_comments),
      ...asArray(data.latest_comments),
      ...asArray(data.top_comments),
      ...asArray((item as { data?: Record<string, unknown> }).data?.comments),
      ...asArray((item as { data?: Record<string, unknown> }).data?.post_comments),
    ];

    for (const comment of commentSources) {
      const author = ((comment.author ?? comment.user ?? comment.actor ?? comment.commenter) as Record<string, unknown>) ?? {};
      const first = String(author.firstName ?? '').trim();
      const last = String(author.lastName ?? '').trim();
      const fromParts = `${first} ${last}`.trim();
      const name = String(
        comment.authorName ??
        comment.author_name ??
        comment.profileName ??
        comment.profile_name ??
        comment.name ??
        author.name ??
        author.fullName ??
        author.full_name ??
        fromParts
      ).trim();
      const linkedinUrl = extractLinkedInUrl(String(
        comment.authorProfileUrl ??
        comment.author_profile_url ??
        comment.profileUrl ??
        comment.profile_url ??
        comment.authorUrl ??
        comment.author_url ??
        author.url ??
        author.profileUrl ??
        author.profile_url ??
        author.linkedinUrl ??
        author.linkedin_url ??
        ''
      ).trim());
      const headline = String(comment.headline ?? author.headline ?? comment.jobTitle ?? '').trim();
      const company = String(
        comment.company ??
        comment.company_name ??
        author.company ??
        author.company_name ??
        extractCompanyFromHeadline(headline)
      ).trim();
      const title = String(comment.title ?? comment.jobTitle ?? author.title ?? headline).trim();
      const commentText = String(
        comment.commentText ??
        comment.comment_text ??
        comment.text ??
        comment.body ??
        comment.content ??
        comment.message ??
        ''
      ).trim();

      push({ name, company, title, linkedinUrl, commentText, sourceType: 'commenter' });
      if (out.length >= max) return out;
    }
  }

  return out;
}

function extractLinkedInUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (!raw.toLowerCase().includes('linkedin.com')) return '';
  return raw;
}

function normalizeLinkedInPersonProfileUrl(value: unknown): string {
  const raw = extractLinkedInUrl(value);
  if (!raw) return '';
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withProtocol);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (host !== 'linkedin.com') return '';
    const path = url.pathname.replace(/\/+$/, '');
    const match = path.match(/^\/in\/[^/?#]+/i);
    if (!match) return '';
    return `https://www.linkedin.com${match[0]}`;
  } catch {
    const match = withProtocol.match(/linkedin\.com\/in\/[^/?#]+/i);
    if (!match) return '';
    const m = match[0].replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    return `https://${m}`;
  }
}

function extractFacebookUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withProtocol);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (!host.includes('facebook.com')) return '';
    const pathname = url.pathname.replace(/\/+$/, '');
    const profileId = url.searchParams.get('id')?.trim();
    if (profileId) return `https://www.facebook.com/profile.php?id=${profileId}`;
    const peopleMatch = pathname.match(/^\/people\/[^/]+\/(\d+)$/i);
    if (peopleMatch?.[1]) return `https://www.facebook.com/profile.php?id=${peopleMatch[1]}`;
    if (!pathname || pathname === '/') return '';
    return `https://www.facebook.com${pathname}`;
  } catch {
    if (!withProtocol.toLowerCase().includes('facebook.com')) return '';
    const profileIdMatch = withProtocol.match(/[?&]id=(\d+)/i);
    if (profileIdMatch?.[1]) return `https://www.facebook.com/profile.php?id=${profileIdMatch[1]}`;
    const peopleMatch = withProtocol.match(/facebook\.com\/people\/[^/]+\/(\d+)/i);
    if (peopleMatch?.[1]) return `https://www.facebook.com/profile.php?id=${peopleMatch[1]}`;
    return withProtocol.replace(/\/+$/, '');
  }
}

function buildFacebookProfileUrlFromParts(profileUrl: unknown, username: unknown, userId: unknown): string {
  const direct = extractFacebookUrl(profileUrl);
  if (direct) return direct;

  const normalizedUsername = String(username ?? '').trim().replace(/^@+/, '');
  if (normalizedUsername) {
    return `https://www.facebook.com/${normalizedUsername}`;
  }

  const normalizedUserId = String(userId ?? '').trim();
  if (normalizedUserId) {
    return `https://www.facebook.com/${normalizedUserId}`;
  }

  return '';
}

function deriveFacebookDisplayName(name: unknown, username: unknown, userId: unknown): string {
  const normalizedName = String(name ?? '').trim();
  if (normalizedName) return normalizedName;

  const normalizedUsername = String(username ?? '').trim().replace(/^@+/, '');
  if (normalizedUsername) return normalizedUsername;

  const normalizedUserId = String(userId ?? '').trim();
  if (normalizedUserId) return normalizedUserId;

  return 'Unknown commenter';
}

function getFacebookIdentityKey(value: unknown): string {
  const facebookUrl = extractFacebookUrl(value);
  if (!facebookUrl) return '';
  const profileIdMatch = facebookUrl.match(/[?&]id=(\d+)/i);
  if (profileIdMatch?.[1]) return `fbid:${profileIdMatch[1]}`;
  try {
    const url = new URL(facebookUrl);
    const path = url.pathname.replace(/\/+$/, '').toLowerCase();
    if (!path || path === '/') return '';
    return `path:${path}`;
  } catch {
    return facebookUrl.trim().toLowerCase();
  }
}

function chooseRicherFacebookProfile(
  current: FacebookCommentProfile & { postUrl?: string },
  candidate: FacebookCommentProfile & { postUrl?: string },
): FacebookCommentProfile & { postUrl?: string } {
  const merged = { ...current };
  if (!merged.name && candidate.name) merged.name = candidate.name;
  if (!merged.company && candidate.company) merged.company = candidate.company;
  if (!merged.email && candidate.email) merged.email = candidate.email;
  if ((!merged.commentText || merged.sourceType === 'post_author') && candidate.commentText) {
    merged.commentText = candidate.commentText;
  }
  if ((!merged.facebookUrl || /profile\.php\?id=/i.test(merged.facebookUrl)) && candidate.facebookUrl) {
    merged.facebookUrl = candidate.facebookUrl;
  }
  if (!merged.postUrl && candidate.postUrl) merged.postUrl = candidate.postUrl;
  if (merged.sourceType !== 'commenter' && candidate.sourceType === 'commenter') {
    merged.sourceType = candidate.sourceType;
  }
  return merged;
}

function extractAuthorProfile(item: Record<string, unknown>): LinkedInCommentProfile | null {
  const data = asRecord(item.data);
  const author = ((item.author ?? item.user ?? item.actor ?? item.profile ?? data.author ?? data.user ?? data.actor ?? data.profile) as Record<string, unknown>) ?? {};
  const name = String(
    item.authorName ??
    item.author_name ??
    data.authorName ??
    data.author_name ??
    author.name ??
    author.fullName ??
    author.full_name ??
    item.name ??
    data.name ??
    ''
  ).trim();
  const linkedinUrl = normalizeLinkedInPersonProfileUrl(
    item.authorProfileUrl ??
    item.author_profile_url ??
    item.authorUrl ??
    item.author_url ??
    item.profileUrl ??
    item.profile_url ??
    item.url ??
    data.authorProfileUrl ??
    data.author_profile_url ??
    data.authorUrl ??
    data.author_url ??
    data.profileUrl ??
    data.profile_url ??
    data.url ??
    author.url ??
    author.profileUrl ??
    author.profile_url ??
    author.linkedinUrl ??
    author.linkedin_url
  );
  const company = String(item.company ?? item.company_name ?? data.company ?? data.company_name ?? author.company ?? author.company_name ?? '').trim();
  const title = String(item.title ?? item.headline ?? data.title ?? data.headline ?? author.title ?? author.headline ?? '').trim();
  if (!name && !linkedinUrl) return null;
  return { name, company, title, linkedinUrl };
}

function extractAuthorCandidate(item: Record<string, unknown>): LinkedInAuthorCandidate | null {
  const author = extractAuthorProfile(item);
  const postUrl = getPostUrl(item);
  if (!author || !author.linkedinUrl || !postUrl) return null;
  return {
    ...author,
    postUrl,
    commentCount: extractCommentCount(item),
  };
}

function extractFacebookAuthorProfile(item: Record<string, unknown>): FacebookCommentProfile | null {
  const data = asRecord(item.data);
  const author = ((item.author ?? item.user ?? item.actor ?? item.profile ?? data.author ?? data.user ?? data.actor ?? data.profile) as Record<string, unknown>) ?? {};
  const rawName = String(
    item.authorName ??
    item.author_name ??
    item.author_username ??
    data.authorName ??
    data.author_name ??
    data.author_username ??
    author.name ??
    author.fullName ??
    author.full_name ??
    author.username ??
    author.userName ??
    item.name ??
    data.name ??
    ''
  ).trim();
  const rawUsername = String(
    item.author_username ??
    data.author_username ??
    author.username ??
    author.userName ??
    ''
  ).trim();
  const rawUserId = String(
    item.author_user_id ??
    item.authorUserId ??
    data.author_user_id ??
    data.authorUserId ??
    author.id ??
    author.userId ??
    author.user_id ??
    ''
  ).trim();
  const facebookUrl = buildFacebookProfileUrlFromParts(
    item.authorProfileUrl ??
    item.author_profile_url ??
    item.authorUrl ??
    item.author_url ??
    item.profileUrl ??
    item.profile_url ??
    data.authorProfileUrl ??
    data.author_profile_url ??
    data.authorUrl ??
    data.author_url ??
    data.profileUrl ??
    data.profile_url ??
    author.url ??
    author.profileUrl ??
    author.profile_url ??
    author.facebookUrl ??
    author.facebook_url,
    rawUsername,
    rawUserId,
  );
  const name = deriveFacebookDisplayName(rawName, rawUsername, rawUserId);
  const company = String(item.company ?? item.company_name ?? data.company ?? data.company_name ?? author.company ?? author.company_name ?? '').trim();
  const email = String(item.email ?? data.email ?? author.email ?? '').trim();
  if (!name && !facebookUrl) return null;
  return { name, company, facebookUrl, email };
}

function extractFacebookCommentProfiles(items: Array<Record<string, unknown>>, max = 50): FacebookCommentProfile[] {
  const out: FacebookCommentProfile[] = [];
  const seen = new Set<string>();
  const push = (candidate: FacebookCommentProfile) => {
    const name = candidate.name.trim() || 'Unknown commenter';
    const facebookUrl = candidate.facebookUrl.trim();
    const key = getFacebookIdentityKey(facebookUrl) || facebookUrl.trim().toLowerCase() || name.toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({
      name,
      company: candidate.company.trim(),
      facebookUrl,
      email: candidate.email?.trim() || '',
      commentText: candidate.commentText?.trim() || '',
      sourceType: candidate.sourceType,
    });
  };

  for (const item of items) {
    const data = asRecord(item.data);
    const commentSources = [
      ...asArray(item.comments),
      ...asArray(item.post_comments),
      ...asArray(item.latest_comments),
      ...asArray(item.top_comments),
      ...asArray(data.comments),
      ...asArray(data.post_comments),
      ...asArray(data.latest_comments),
      ...asArray(data.top_comments),
    ];
    for (const comment of commentSources) {
      const author = ((comment.author ?? comment.user ?? comment.actor ?? comment.commenter) as Record<string, unknown>) ?? {};
      const rawName = String(
        comment.authorName ??
        comment.author_name ??
        comment.author_username ??
        comment.profileName ??
        comment.profile_name ??
        comment.name ??
        author.name ??
        author.fullName ??
        author.full_name ??
        author.username ??
        author.userName ??
        ''
      ).trim();
      const rawUsername = String(
        comment.author_username ??
        comment.authorUsername ??
        author.username ??
        author.userName ??
        ''
      ).trim();
      const rawUserId = String(
        comment.author_user_id ??
        comment.authorUserId ??
        author.id ??
        author.userId ??
        author.user_id ??
        ''
      ).trim();
      const facebookUrl = buildFacebookProfileUrlFromParts(
        comment.authorProfileUrl ??
        comment.author_profile_url ??
        comment.profileUrl ??
        comment.profile_url ??
        comment.authorUrl ??
        comment.author_url ??
        author.url ??
        author.profileUrl ??
        author.profile_url ??
        author.facebookUrl ??
        author.facebook_url ??
        '',
        rawUsername,
        rawUserId,
      );
      const name = deriveFacebookDisplayName(rawName, rawUsername, rawUserId);
      const company = String(
        comment.company ??
        comment.company_name ??
        author.company ??
        author.company_name ??
        ''
      ).trim();
      const email = String(comment.email ?? author.email ?? '').trim();
      const commentText = String(
        comment.commentText ??
        comment.comment_text ??
        comment.text ??
        comment.body ??
        comment.content ??
        comment.message ??
        ''
      ).trim();
      push({ name, company, facebookUrl, email, commentText, sourceType: 'commenter' });
      if (out.length >= max) return out;
    }
  }
  return out;
}

function extractProfileLocation(item: Record<string, unknown>): string {
  const profile = asRecord(item.profile);
  const author = asRecord(item.author);
  const topCard = asRecord(item.topCard ?? item.top_card);
  const geo = asRecord(item.geo);
  const address = asRecord(item.address);
  const locationCandidates = [
    item.location,
    item.locationName,
    item.geoLocationName,
    item.geo_location_name,
    item.country,
    item.countryName,
    item.addressCountry,
    item.address_country,
    profile.location,
    profile.locationName,
    profile.country,
    author.location,
    author.locationName,
    topCard.location,
    topCard.geoLocationName,
    geo.full,
    geo.name,
    geo.country,
    address.country,
    address.countryName,
  ];
  for (const candidate of locationCandidates) {
    const value = String(candidate ?? '').trim();
    if (value) return value;
  }
  return '';
}

function extractScrapedAuthorProfile(item: Record<string, unknown>): ScrapedAuthorProfile | null {
  const author = extractAuthorProfile(item);
  if (!author || !author.linkedinUrl) return null;
  return {
    ...author,
    location: extractProfileLocation(item),
  };
}

function normalizeLocationText(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9\s,.-]/g, ' ').replace(/\s+/g, ' ').trim();
}

function computeLocationScore(target: string, actual: string): number {
  const wanted = normalizeLocationText(target);
  if (!wanted) return 0;
  const observed = normalizeLocationText(actual);
  if (!observed) return 0;
  if (observed === wanted) return 5;
  if (observed.includes(wanted) || wanted.includes(observed)) return 4;
  const wantedParts = wanted.split(/[,\s]+/).filter((part) => part.length >= 3);
  const matchedParts = wantedParts.filter((part) => observed.includes(part));
  if (matchedParts.length >= 2) return 3;
  if (matchedParts.length === 1) return 2;
  return 0;
}

function scoreAuthorPost(targetLocation: string, authorLocation: string, commentCount: number): number {
  const locationScore = computeLocationScore(targetLocation, authorLocation);
  return locationScore * 1000 + Math.max(0, commentCount);
}

function extractCommentCount(item: Record<string, unknown>): number {
  const data = asRecord(item.data);
  const stats = asRecord(item.stats);
  const dataStats = asRecord(data.stats);
  const candidates = [
    item.commentsCount,
    item.commentCount,
    item.comments_count,
    item.numComments,
    item.totalComments,
    data.commentsCount,
    data.commentCount,
    data.comments_count,
    data.numComments,
    data.totalComments,
    stats.comments,
    stats.commentCount,
    stats.comments_count,
    dataStats.comments,
    dataStats.commentCount,
    dataStats.comments_count,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const comments = [
    ...asArray(item.comments),
    ...asArray(item.post_comments),
    ...asArray(item.latest_comments),
    ...asArray(item.top_comments),
  ];
  return comments.length;
}

function normalizeLanguageCode(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (raw.startsWith('english')) return 'en';
  const twoLetter = raw.match(/^[a-z]{2}/)?.[0] || '';
  return twoLetter || raw;
}

function getLanguageCodeFromRow(item: Record<string, unknown>): string {
  const data = asRecord(item.data);
  const profile = asRecord(item.profile);
  const topCard = asRecord(item.topCard ?? item.top_card);
  const candidate =
    item.language ??
    item.lang ??
    item.languageCode ??
    item.detectedLanguage ??
    item.detected_language ??
    item.postLanguage ??
    item.locale ??
    data.language ??
    data.lang ??
    data.languageCode ??
    data.detectedLanguage ??
    data.detected_language ??
    data.postLanguage ??
    data.locale ??
    profile.language ??
    topCard.language;
  return normalizeLanguageCode(candidate);
}

function getLikelyPostText(item: Record<string, unknown>): string {
  const data = asRecord(item.data);
  const candidates = [
    item.text,
    item.content,
    item.message,
    item.caption,
    item.postText,
    item.post_text,
    item.description,
    item.title,
    data.text,
    data.content,
    data.message,
    data.caption,
    data.postText,
    data.post_text,
    data.description,
    data.title,
  ];
  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (value) return value;
  }
  return '';
}

function isLikelyEnglishText(text: string): boolean {
  const sample = text.trim().toLowerCase();
  if (!sample) return false;
  const words = sample.split(/\s+/).filter(Boolean);
  if (words.length < 4) return false;
  const common = [' the ', ' and ', ' to ', ' of ', ' in ', ' for ', ' with ', ' on ', ' this ', ' that '];
  const padded = ` ${sample} `;
  const matches = common.reduce((acc, token) => acc + (padded.includes(token) ? 1 : 0), 0);
  const asciiLetters = (sample.match(/[a-z]/g) || []).length;
  const nonAscii = (sample.match(/[^\x00-\x7F]/g) || []).length;
  return (matches >= 2 && asciiLetters >= Math.max(30, words.length * 2)) || (matches >= 1 && nonAscii === 0 && asciiLetters >= 60);
}

function isEnglishLinkedInRow(item: Record<string, unknown>): boolean {
  const language = getLanguageCodeFromRow(item);
  if (language) return language === 'en';
  return isLikelyEnglishText(getLikelyPostText(item));
}

function toLinkedInSearchQuery(raw: string, exactMatch: boolean): string {
  const input = raw.trim();
  if (!input || !exactMatch) return input;
  const parts = input.split(/\s+(OR|AND)\s+/i);
  return parts
    .map((part) => {
      const token = part.trim();
      if (!token) return token;
      if (/^(OR|AND)$/i.test(token)) return token.toUpperCase();
      if (token.includes('"')) return token;
      return `"${token}"`;
    })
    .join(' ');
}

function toProfileKey(profile: LinkedInCommentProfile): string {
  const li = String(profile.linkedinUrl ?? '').trim().toLowerCase();
  if (li) return `li:${li}`;
  return `nm:${String(profile.name ?? '').trim().toLowerCase()}|co:${String(profile.company ?? '').trim().toLowerCase()}`;
}

function buildLinkedinListenerTags(searchAttemptType: string): string[] {
  const base = ['LinkedIn Listener', 'One Click', 'Scraped'];
  const attempt = String(searchAttemptType ?? '').trim().toLowerCase();
  const add = (tag: string) => {
    if (!base.includes(tag)) base.push(tag);
  };
  if (attempt.includes('full_name_company_title')) {
    add('Search Param: Full Name');
    add('Search Param: Company Name');
    add('Search Param: Title');
  } else if (attempt.includes('full_name_linkedin_keyword')) {
    add('Search Param: Full Name');
    add('Search Param: LinkedIn URL Keyword');
  } else if (attempt.includes('full_name_company')) {
    add('Search Param: Full Name');
    add('Search Param: Company Name');
  } else if (attempt.includes('full_name_only')) {
    add('Search Param: Full Name');
  } else if (attempt.includes('keyword_linkedin_url')) {
    add('Search Param: LinkedIn URL Keyword');
  }
  return base;
}

function extractFirstEmail(value: unknown): string {
  if (!value) return '';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  const match = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match ? match[0] : '';
}

function normalizeWebsiteUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
  return '';
}

function normalizeLinkedinProfileUrl(value: unknown): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower.includes('linkedin.com/sales/') || lower.includes('linkedin.com/talent/')) return '';
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  const match = withProtocol.match(/linkedin\.com\/in\/[^/?#]+/i);
  if (match) return `https://www.${match[0].replace(/^https?:\/\//i, '')}`;
  return withProtocol.split('#')[0]!.split('?')[0]!;
}

function normalizeAnyUrl(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getContactName(contact: OneClickContact): string {
  const fromFull = String(contact.fullName ?? contact.Name ?? '').trim();
  if (fromFull) return fromFull;
  const first = String(contact.firstName ?? '').trim();
  const last = String(contact.lastName ?? '').trim();
  return `${first} ${last}`.trim();
}

function getContactCompany(contact: OneClickContact): string {
  return String(contact.company ?? contact.Company ?? '').trim();
}

function getContactSocialUrl(contact: OneClickContact, channel: OneClickChannel): string {
  switch (channel) {
    case 'linkedin':
      return normalizeLinkedinProfileUrl(contact.linkedIn ?? contact.LIProfileUrl ?? '');
    case 'facebook':
      return normalizeAnyUrl(contact.facebook_url ?? contact.facebookUrl ?? '');
    case 'instagram':
      return normalizeAnyUrl(contact.instagram_url ?? contact.instagramUrl ?? '');
    case 'tiktok':
      return normalizeAnyUrl(contact.tiktok_url ?? contact.tiktokUrl ?? '');
    case 'x':
      return normalizeAnyUrl(contact.x_url ?? contact.xUrl ?? contact.twitterUrl ?? '');
    case 'substack':
      return normalizeAnyUrl(contact.substack_url ?? contact.substackUrl ?? '');
    default:
      return '';
  }
}

function getContactSocialDatasetIds(contact: OneClickContact, platform: ContactSocialPlatform): { profile?: string; posts?: string } {
  const asList = (value: unknown): string[] => (Array.isArray(value) ? value.map((v) => String(v ?? '').trim()).filter(Boolean) : []);
  const c = contact as Record<string, unknown>;
  if (platform === 'linkedin') {
    const profileIds = asList(c.linkedin_profile_ds);
    const postsIds = asList(c.linkedin_posts_ds);
    return {
      profile: profileIds.length ? profileIds[profileIds.length - 1] : undefined,
      posts: postsIds.length ? postsIds[postsIds.length - 1] : undefined,
    };
  }
  const platformDatasetFields: Record<Exclude<ContactSocialPlatform, 'linkedin'>, string[]> = {
    facebook: ['facebook_ds', 'facebook_profile_ds', 'facebook_profile_posts_ds'],
    instagram: ['instagram_ds', 'instagram_profile_ds'],
    tiktok: ['tiktok_ds', 'tiktok_profile_ds'],
    x: ['x_ds', 'twitter_ds', 'x_profile_ds'],
  };
  const fieldCandidates = platformDatasetFields[platform] ?? [];
  const merged: string[] = [];
  for (const field of fieldCandidates) {
    merged.push(...asList(c[field]));
  }
  const profile = merged.length ? merged[merged.length - 1] : undefined;
  return {
    profile,
  };
}

function channelToSocialPlatform(channel: OneClickChannel): ContactSocialPlatform | null {
  if (channel === 'substack') return null;
  return channel;
}

function toOneClickContactFromResearchData(
  value: unknown,
  extras?: { searchResultId?: string; contactId?: string }
): OneClickContact {
  const data = asRecord(value);
  const researchedData = asRecord(data.researchedData);
  return {
    ...data,
    ...researchedData,
    searchResultId:
      extras?.searchResultId ||
      String(data.searchResultId ?? researchedData.searchResultId ?? '').trim() ||
      undefined,
    contactId:
      extras?.contactId ||
      String(data.contactId ?? data.id ?? researchedData.contactId ?? researchedData.id ?? '').trim() ||
      undefined,
  } as OneClickContact;
}

function toOneClickContactFromRecentItem(
  item: Record<string, unknown>,
  source: 'linkedin' | 'facebook'
): OneClickContact {
  const matchedLead = asRecord(item.matchedLead);
  const searchResultId = String(item.matchedSearchResultId ?? '').trim() || undefined;
  const contactId =
    String(item.matchedContactSearchId ?? matchedLead.contactSearchId ?? '').trim() || undefined;

  const base = toOneClickContactFromResearchData(item, { searchResultId, contactId });
  const fromMatchedLead =
    Object.keys(matchedLead).length > 0
      ? toOneClickContactFromResearchData(matchedLead, { searchResultId, contactId })
      : ({} as OneClickContact);

  const merged: OneClickContact = {
    ...base,
    ...fromMatchedLead,
    searchResultId: searchResultId ?? base.searchResultId ?? fromMatchedLead.searchResultId,
    contactId: contactId ?? base.contactId ?? fromMatchedLead.contactId,
    fullName:
      String(base.fullName ?? fromMatchedLead.fullName ?? item.name ?? '').trim() || undefined,
    company:
      String(base.company ?? fromMatchedLead.company ?? item.company ?? '').trim() || undefined,
  };

  const social = Array.isArray(matchedLead.social) ? matchedLead.social : [];
  for (const row of social) {
    const entry = asRecord(row);
    const type = String(entry.type ?? '').trim().toLowerCase();
    const url = normalizeAnyUrl(entry.url);
    if (!url) continue;
    if (type === 'linkedin') {
      merged.linkedIn = merged.linkedIn || url;
      merged.LIProfileUrl = merged.LIProfileUrl || url;
    } else if (type === 'facebook') {
      merged.facebook_url = merged.facebook_url || url;
      merged.facebookUrl = merged.facebookUrl || url;
    } else if (type === 'instagram') {
      merged.instagram_url = merged.instagram_url || url;
      merged.instagramUrl = merged.instagramUrl || url;
    } else if (type === 'tiktok') {
      merged.tiktok_url = merged.tiktok_url || url;
      merged.tiktokUrl = merged.tiktokUrl || url;
    } else if (type === 'x' || type === 'twitter') {
      merged.x_url = merged.x_url || url;
      merged.xUrl = merged.xUrl || url;
      merged.twitterUrl = merged.twitterUrl || url;
    } else if (type === 'substack') {
      merged.substack_url = merged.substack_url || url;
      merged.substackUrl = merged.substackUrl || url;
    }
  }

  const sourceProfile = normalizeAnyUrl(item.profileUrl);
  if (source === 'linkedin' && sourceProfile) {
    merged.linkedIn = merged.linkedIn || sourceProfile;
    merged.LIProfileUrl = merged.LIProfileUrl || sourceProfile;
  }
  if (source === 'facebook' && sourceProfile) {
    merged.facebook_url = merged.facebook_url || sourceProfile;
    merged.facebookUrl = merged.facebookUrl || sourceProfile;
  }

  return merged;
}

function humanizeOutcomeReason(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return 'No match in search index';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const inFlightRunPolls = new Map<string, Promise<Array<Record<string, unknown>>>>();

async function pollRunResults(
  runId: string,
  payload: Record<string, unknown>,
  onProgress?: (message: string) => void,
  options?: { maxAttempts?: number; pollIntervalMs?: number; allowPartialResultsOnTimeout?: boolean },
): Promise<Array<Record<string, unknown>>> {
  const existing = inFlightRunPolls.get(runId);
  if (existing) return existing;

  const pollPromise = (async () => {
  const maxAttempts = options?.maxAttempts && options.maxAttempts > 0 ? options.maxAttempts : 40;
  const pollIntervalMs = options?.pollIntervalMs && options.pollIntervalMs > 0 ? options.pollIntervalMs : 3000;
  const allowPartialResultsOnTimeout = options?.allowPartialResultsOnTimeout ?? false;
  let datasetId: string | null = null;

  const fetchResults = async () => {
    if (!datasetId) return [];
    const resultsRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/get_results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataset_id: datasetId, run_id: runId, ...payload }),
    });
    const resultsData = await resultsRes.json();
    return Array.isArray(resultsData?.results) ? resultsData.results : [];
  };

  for (let i = 0; i < maxAttempts; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    const statusRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/check_status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId }),
    });
    const statusData = await statusRes.json();
    const status = String(statusData?.status ?? 'RUNNING');
    datasetId = (statusData?.dataset_id ?? datasetId ?? null) as string | null;

    onProgress?.(`Polling run (${i + 1}/${maxAttempts})…`);

    if (status === 'FAILED') throw new Error('Run failed');
    if (status === 'SUCCEEDED' && datasetId) {
      return fetchResults();
    }
  }

  if (allowPartialResultsOnTimeout && datasetId) {
    const partialResults = await fetchResults();
    if (partialResults.length > 0) {
      onProgress?.(`Polling timed out. Using ${partialResults.length} partial results…`);
      return partialResults;
    }
  }

  throw new Error('Run polling timed out');
  })();

  inFlightRunPolls.set(runId, pollPromise);
  try {
    return await pollPromise;
  } finally {
    if (inFlightRunPolls.get(runId) === pollPromise) {
      inFlightRunPolls.delete(runId);
    }
  }
}

async function startDeepScrapeRun(userEmail: string, urls: string[]) {
  const startDeepRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners/deep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail,
      platform: 'linkedin',
      scraper: 'linkedin_post_urls',
      urls,
      maxItems: 250,
      scrapeReplies: true,
      profileScraperMode: 'short',
    }),
  });
  const startDeep = await startDeepRes.json();
  if (!startDeep?.success || !startDeep?.run_id) {
    throw new Error(startDeep?.error || 'Failed to start deep scrape');
  }
  return String(startDeep.run_id);
}

async function startFacebookDeepScrapeRun(userEmail: string, urls: string[]) {
  const response = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners/deep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail,
      platform: 'facebook',
      scraper: 'facebook_post_urls',
      urls,
      limitPerSource: 20,
      deepScrape: true,
      rawData: true,
      comments_limit: 50,
      commentsLimit: 50,
      maxComments: 50,
    }),
  });
  const json = await response.json();
  if (!json?.success || !json?.run_id) {
    throw new Error(json?.error || 'Failed to start Facebook deep scrape');
  }
  return String(json.run_id);
}

async function startFacebookProfileScrape(userEmail: string, profileUrl: string) {
  const response = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/facebook`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail,
      company_name: profileUrl,
      page_url: profileUrl,
      force_refresh: true,
    }),
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to start Facebook profile scrape');
  }
  return {
    runId: String(json.page_run_id ?? json.run_id ?? ''),
    datasetId: String(json.page_dataset_id ?? json.dataset_id ?? ''),
  };
}

function extractFacebookProfileDetails(items: Array<Record<string, unknown>>, fallbackUrl: string): FacebookProfileDetails {
  for (const item of items) {
    const data = asRecord(item.data);
    const source = [item, data].find(Boolean) as Record<string, unknown>;
    const name = String(
      source.pageName ??
      source.title ??
      source.name ??
      source.fullName ??
      source.full_name ??
      source.profileName ??
      source.profile_name ??
      ''
    ).trim();
    const company = String(
      source.company ??
      source.company_name ??
      source.category ??
      ''
    ).trim();
    const email = String(
      source.email ??
      source.contactEmail ??
      source.contact_email ??
      source.publicEmail ??
      source.public_email ??
      extractFirstEmail(source.about ?? source.bio ?? source.description ?? '') ??
      ''
    ).trim();
    const website = normalizeWebsiteUrl(
      source.website ??
      source.link ??
      source.externalUrl ??
      source.external_url ??
      ''
    );
    const about = String(source.about ?? source.bio ?? source.description ?? '').trim();
    const facebookUrl = extractFacebookUrl(
      source.pageUrl ??
      source.facebookUrl ??
      source.facebook_url ??
      source.url ??
      fallbackUrl
    ) || fallbackUrl;
    if (name || email) {
      return { name, company, email, facebookUrl, website, about };
    }
  }
  return {
    name: '',
    company: '',
    email: '',
    facebookUrl: fallbackUrl,
    website: '',
    about: '',
  };
}

async function enrichFacebookProfile(userEmail: string, profileUrl: string): Promise<FacebookProfileDetails | null> {
  const started = await startFacebookProfileScrape(userEmail, profileUrl);
  if (!started.runId || !started.datasetId) return null;
  await pollRunResults(started.runId, { source: 'facebook_profile', profileUrl });
  const resultsRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/get_results`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dataset_id: started.datasetId, run_id: started.runId, source: 'facebook_profile', profileUrl }),
  });
  const resultsJson = await resultsRes.json();
  const items = Array.isArray(resultsJson?.results) ? resultsJson.results as Array<Record<string, unknown>> : [];
  return extractFacebookProfileDetails(items, profileUrl);
}

async function startAuthorProfileScrapeRun(userEmail: string, urls: string[]) {
  const response = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners/deep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail,
      platform: 'linkedin',
      scraper: 'linkedin_profile',
      urls,
    }),
  });
  const json = await response.json();
  if (!json?.success || !json?.run_id) {
    throw new Error(json?.error || 'Failed to start author profile scrape');
  }
  return String(json.run_id);
}

export function AIEngagementProtocol() {
  const { userEmail, accessPepaiResearch, addScrapeJob, updateScrapeJob } = useAppStore();
  const [activeTab, setActiveTab] = useState<PepTab>('profile');
  const [linkedinListenerPageTab, setLinkedinListenerPageTab] = useState<LinkedinListenerPageTab>('listener');
  const location = useLocation();
  const isLinkedinListenerPage = location.pathname === '/listening-linkedin';

  useEffect(() => {
    if (location.pathname === '/listening-linkedin') {
      setActiveTab('linkedin_generation');
      return;
    }
    if (location.pathname === '/listening-facebook') {
      setActiveTab('facebook_generation');
      return;
    }

    // Backward compatibility for old deep links
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'linkedin_generation' || tab === 'facebook_generation') {
      setActiveTab(tab);
      return;
    }

    setActiveTab('profile');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isLinkedinListenerPage) {
      setLinkedinListenerPageTab('listener');
    }
  }, [isLinkedinListenerPage]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [profile, setProfile] = useState<PepProfile>(EMPTY_PROFILE); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [_error, setError] = useState(''); // profile load/save still references
  const [_notice, setNotice] = useState(''); // profile load/save still references

  const [generationKeyword, setGenerationKeyword] = useState('lead generation');
  const [generationLoading, setGenerationLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [generationStats, setGenerationStats] = useState<LinkedInGenerationStats>(emptyStats);
  const [generationCurrentPost, setGenerationCurrentPost] = useState(0);
  const [generationDateFilter, setGenerationDateFilter] = useState('past-week');
  const [generationIndustryUrn, setGenerationIndustryUrn] = useState('');
  const [generationAuthorJobTitle, setGenerationAuthorJobTitle] = useState('');
  const [generationAuthorLocation, setGenerationAuthorLocation] = useState('');
  const [generationAuthorKeywords, setGenerationAuthorKeywords] = useState('');
  const [generationAuthorUrlsText, setGenerationAuthorUrlsText] = useState('');
  const [generationAuthorsCompaniesText, setGenerationAuthorsCompaniesText] = useState('');
  const [generationAuthorsIndustryIds, setGenerationAuthorsIndustryIds] = useState<string[]>([]);
  const [generationMentioningMemberText, setGenerationMentioningMemberText] = useState('');
  const [generationMentioningCompanyText, setGenerationMentioningCompanyText] = useState('');
  const [generationExactMatch, setGenerationExactMatch] = useState(true);
  const [generationPostCount, setGenerationPostCount] = useState(5);
  const [generationCommentsPostedLimit, setGenerationCommentsPostedLimit] = useState('');
  const [generationPostedLimit, setGenerationPostedLimit] = useState('week');
  const [generationPostedLimitDate, setGenerationPostedLimitDate] = useState('');
  const [generationContentType, setGenerationContentType] = useState('all');
  const [generationSortBy, setGenerationSortBy] = useState('date');
  const [generationStartPage, setGenerationStartPage] = useState(1);
  const [generationScrapePages, setGenerationScrapePages] = useState(1);
  const [generationMaxComments, setGenerationMaxComments] = useState(100);
  const [generationMaxPosts, setGenerationMaxPosts] = useState(20);
  const [generationMaxReactions, setGenerationMaxReactions] = useState(100);
  const [generationProfileScraperMode, setGenerationProfileScraperMode] = useState('short');
  const [generationCommentsProfileScraperMode, setGenerationCommentsProfileScraperMode] = useState('short');
  const [generationReactionsProfileScraperMode, setGenerationReactionsProfileScraperMode] = useState('short');
  const [generationScrapeComments, setGenerationScrapeComments] = useState(true);
  const [generationScrapeReactions, setGenerationScrapeReactions] = useState(true);
  const [generationPostNestedComments, setGenerationPostNestedComments] = useState(false);
  const [generationPostNestedReactions, setGenerationPostNestedReactions] = useState(false);
  const [linkedinSpecificPostUrlsText, setLinkedinSpecificPostUrlsText] = useState('');
  const [generationPosts, setGenerationPosts] = useState<LinkedInSelectedPost[]>([]);
  const [generationProfiles, setGenerationProfiles] = useState<Array<LinkedInCommentProfile & { postUrl?: string }>>([]);
  const [generationOutcomes, setGenerationOutcomes] = useState<LinkedInGenerationOutcome[]>([]);
  const [linkedinLeadGenTab, setLinkedinLeadGenTab] = useState<LinkedinLeadGenTab>('one_click_lead_gen');
  const [trendKeywordsText, setTrendKeywordsText] = useState('saas leads\nsaas sales');
  const [trendDateFilter, setTrendDateFilter] = useState('past-2-weeks');
  const [trendExactMatch, setTrendExactMatch] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendRows, setTrendRows] = useState<LinkedInTrendRow[]>([]);
  const [trendError, setTrendError] = useState('');
  const [linkedinCommentTaskMode, setLinkedinCommentTaskMode] = useState<LinkedInCommentTaskMode>('specific_post_commenter');
  const [linkedinSharedComment, setLinkedinSharedComment] = useState('');
  const [linkedinCommentDrafts, setLinkedinCommentDrafts] = useState<Record<string, string>>({});
  const [linkedinCommentDelaySeconds, setLinkedinCommentDelaySeconds] = useState(187);
  const [linkedinCommentTaskLoading, setLinkedinCommentTaskLoading] = useState(false);
  const [linkedinCommentTaskStatus, setLinkedinCommentTaskStatus] = useState('');
  const [linkedinCommentTaskError, setLinkedinCommentTaskError] = useState('');
  const [companyPageSearchEnabled, setCompanyPageSearchEnabled] = useState(true);
  const [companyPageSeniorities, setCompanyPageSeniorities] = useState<string[]>(['C-Level', 'VP', 'Director']);
  const [companyPageDepartments, setCompanyPageDepartments] = useState<string[]>([]);
  const [facebookKeyword, setFacebookKeyword] = useState('lead generation');
  const [facebookSpecificPostUrlsText, setFacebookSpecificPostUrlsText] = useState('');
  const [facebookGenerationLoading, setFacebookGenerationLoading] = useState(false);
  const [facebookGenerationStatus, setFacebookGenerationStatus] = useState('');
  const [facebookGenerationError, setFacebookGenerationError] = useState('');
  const [facebookGenerationStats, setFacebookGenerationStats] = useState<LinkedInGenerationStats>(emptyStats);
  const [facebookGenerationCurrentPost, setFacebookGenerationCurrentPost] = useState(0);
  const [facebookDateFilter, setFacebookDateFilter] = useState('past-week');
  const [facebookPostCount, setFacebookPostCount] = useState(5);
  const [facebookPosts, setFacebookPosts] = useState<FacebookSelectedPost[]>([]);
  const [facebookProfiles, setFacebookProfiles] = useState<Array<FacebookCommentProfile & { postUrl?: string }>>([]);
  const [facebookOutcomes, setFacebookOutcomes] = useState<FacebookGenerationOutcome[]>([]);
  const [facebookCommentTaskMode, setFacebookCommentTaskMode] = useState<FacebookCommentTaskMode>('specific_post_commenter');
  const [facebookSharedComment, setFacebookSharedComment] = useState('');
  const [facebookGroupUrlsText, setFacebookGroupUrlsText] = useState('');
  const [facebookCommentDrafts, setFacebookCommentDrafts] = useState<Record<string, string>>({});
  const [facebookCommentDelaySeconds, setFacebookCommentDelaySeconds] = useState(187);
  const [facebookCommentTaskLoading, setFacebookCommentTaskLoading] = useState(false);
  const [facebookCommentTaskStatus, setFacebookCommentTaskStatus] = useState('');
  const [facebookCommentTaskError, setFacebookCommentTaskError] = useState('');
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaignOption[]>([]);
  const [selectedInstantlyCampaignId, setSelectedInstantlyCampaignId] = useState(
    () => safeStorageGet(PEPAI_CAMPAIGN_STORAGE_KEY) || ''
  );
  const [oneClickChannel, setOneClickChannel] = useState<OneClickChannel>('linkedin');
  const [oneClickContactsLoading, setOneClickContactsLoading] = useState(false);
  const [oneClickContactsError, setOneClickContactsError] = useState('');
  const [oneClickContactsByKey, setOneClickContactsByKey] = useState<Record<string, OneClickContact>>({});
  const [oneClickContactsFromResearch, setOneClickContactsFromResearch] = useState<Record<string, OneClickContact>>({});
  const [contactProfileDrawer, setContactProfileDrawer] = useState<{ contact: OneClickContact } | null>(null);
  const [contactSocialDrawer, setContactSocialDrawer] = useState<{ contact: OneClickContact; platform: ContactSocialPlatform } | null>(null);
  const [socialEdit, setSocialEdit] = useState<{ contact: OneClickContact; platform: ContactSocialPlatform } | null>(null);
  const [socialEditValue, setSocialEditValue] = useState('');
  const [contactSocialReport, setContactSocialReport] = useState<{
    contact: OneClickContact;
    platform: ContactSocialPlatform;
    items: unknown[];
  } | null>(null);
  const [contactSocialReportLoading, setContactSocialReportLoading] = useState<Record<string, boolean>>({});
  const [socialLoading, setSocialLoading] = useState<Record<string, boolean>>({});
  const [socialScrapeLoading, setSocialScrapeLoading] = useState<Record<string, boolean>>({});
  const [engagementSaving, setEngagementSaving] = useState<Record<string, boolean>>({});
  const [oneClickScheduleLoading, setOneClickScheduleLoading] = useState<{ linkedin: boolean; facebook: boolean }>({
    linkedin: false,
    facebook: false,
  });

  void loading; void saving; // consumed by PepProfileEditorV2 internally
  const canSave = useMemo(() => Boolean(userEmail && !locked), [userEmail, locked]);
  const canUsePepaiResearch = userEmail.toLowerCase().endsWith('@profitibull.com') || accessPepaiResearch;
  const loadOneClickRecent = useCallback(async () => {
    if (!userEmail) {
      setGenerationOutcomes([]);
      setFacebookOutcomes([]);
      setOneClickContactsFromResearch({});
      setOneClickContactsByKey({});
      return;
    }

    setOneClickContactsLoading(true);
    setOneClickContactsError('');
    try {
      const [linkedinRes, facebookRes] = await Promise.all([
        fetch(`${API_CONFIG.RESEARCH_API}/oneclick-linkedin/recent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, limit: 500 }),
        }),
        fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/recent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, limit: 500 }),
        }),
      ]);

      const linkedinJson = await linkedinRes.json().catch(() => ({}));
      const facebookJson = await facebookRes.json().catch(() => ({}));

      const contactsBySearchResult: Record<string, OneClickContact> = {};
      const contactsByKey: Record<string, OneClickContact> = {};

      if (linkedinRes.ok && linkedinJson?.success && Array.isArray(linkedinJson.items)) {
        const mapped = (linkedinJson.items as Array<Record<string, unknown>>)
          .map((item) => {
            const status = String(item.status ?? '').trim();
            const matchedLead = asRecord(item.matchedLead);
            const searchResultId = String(item.matchedSearchResultId ?? matchedLead.searchResultId ?? '').trim();
            const contact = toOneClickContactFromRecentItem(item, 'linkedin');
            if (searchResultId) {
              contactsBySearchResult[searchResultId] = contact;
              contactsByKey[`search:${searchResultId}`] = contact;
            }
            const contactId = String(contact.contactId ?? contact.id ?? '').trim();
            if (contactId) {
              contactsByKey[`contact:${contactId}`] = contact;
            }
            return {
              runId: String(item.runId ?? '').trim() || undefined,
              profileUrl: String(item.profileUrl ?? '').trim(),
              name: String(item.name ?? matchedLead.name ?? '').trim(),
              company: String(item.company ?? matchedLead.company ?? matchedLead.companyName ?? '').trim(),
              triggerPhrase: String(item.triggerPhrase ?? item.keyword ?? '').trim() || undefined,
              postUrl: String(item.postUrl ?? '').trim(),
              commentText: String(item.commentText ?? '').trim(),
              matched: ['matched', 'researching', 'researched', 'failed'].includes(status),
              researched: status === 'researched',
              searchResultId: searchResultId || undefined,
              contactId,
              searchAttemptType: String(item.searchAttemptType ?? '').trim() || undefined,
              error: String(item.error ?? '').trim() || undefined,
            } as LinkedInGenerationOutcome;
          })
          .filter((outcome) => Boolean(outcome.profileUrl));
        setGenerationOutcomes(mapped);
      } else {
        setGenerationOutcomes([]);
      }

      if (facebookRes.ok && facebookJson?.success && Array.isArray(facebookJson.items)) {
        const mapped = (facebookJson.items as Array<Record<string, unknown>>)
          .map((item) => {
            const status = String(item.status ?? '').trim();
            const searchResultId = String(item.matchedSearchResultId ?? '').trim();
            const contact = toOneClickContactFromRecentItem(item, 'facebook');
            if (searchResultId) {
              contactsBySearchResult[searchResultId] = contact;
              contactsByKey[`search:${searchResultId}`] = contact;
            }
            const contactId = String(contact.contactId ?? contact.id ?? '').trim();
            if (contactId) {
              contactsByKey[`contact:${contactId}`] = contact;
            }
            return {
              runId: String(item.runId ?? '').trim() || undefined,
              profileUrl: String(item.profileUrl ?? '').trim(),
              name: String(item.name ?? '').trim(),
              company: String(item.company ?? '').trim(),
              triggerPhrase: String(item.triggerPhrase ?? item.keyword ?? '').trim() || undefined,
              postUrl: String(item.postUrl ?? '').trim(),
              commentText: String(item.commentText ?? '').trim(),
              matched: ['matched', 'researching', 'researched', 'failed'].includes(status),
              researched: status === 'researched',
              searchResultId: searchResultId || undefined,
              contactId,
              searchAttemptType: String(item.searchAttemptType ?? '').trim() || undefined,
              error: String(item.error ?? '').trim() || undefined,
            } as FacebookGenerationOutcome;
          })
          .filter((outcome) => Boolean(outcome.profileUrl));
        setFacebookOutcomes(mapped);
      } else {
        setFacebookOutcomes([]);
      }

      setOneClickContactsFromResearch(contactsBySearchResult);
      setOneClickContactsByKey(contactsByKey);
    } catch (err) {
      console.error('Failed to load One Click recent rows', err);
      setOneClickContactsError('Failed to load one-click leads');
      setGenerationOutcomes([]);
      setFacebookOutcomes([]);
      setOneClickContactsFromResearch({});
      setOneClickContactsByKey({});
    } finally {
      setOneClickContactsLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    void loadOneClickRecent();
  }, [loadOneClickRecent]);

  const linkedInIndustryGroups = useMemo(() => {
    const groups = new Map<string, Array<{ value: string; label: string }>>();
    for (const row of linkedinIndustryCodes as LinkedInIndustryOption[]) {
      const hierarchy = String(row.hierarchy ?? '').trim();
      const parts = hierarchy.split('>').map((part) => part.trim()).filter(Boolean);
      const group = parts[0] || row.label;
      const optionLabel = parts.length > 1 ? parts.slice(1).join(' > ') : row.label;
      const entry = groups.get(group) ?? [];
      entry.push({ value: String(row.id), label: optionLabel });
      groups.set(group, entry);
    }
    return Array.from(groups.entries())
      .map(([group, options]) => ({
        group,
        options: options.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.group.localeCompare(b.group));
  }, []);
  const facebookGroupUrls = useMemo(
    () =>
      facebookGroupUrlsText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    [facebookGroupUrlsText]
  );
  const facebookSpecificPostUrls = useMemo(
    () =>
      facebookSpecificPostUrlsText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.includes('facebook.com')),
    [facebookSpecificPostUrlsText]
  );
  const linkedinSpecificPostUrls = useMemo(
    () =>
      linkedinSpecificPostUrlsText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.includes('linkedin.com')),
    [linkedinSpecificPostUrlsText]
  );
  const generationAuthorUrls = useMemo(
    () =>
      parseMultilineOrCommaList(generationAuthorUrlsText).filter((line) =>
        line.toLowerCase().includes('linkedin.com/')
      ),
    [generationAuthorUrlsText]
  );
  const generationAuthorsCompanies = useMemo(
    () => parseMultilineOrCommaList(generationAuthorsCompaniesText),
    [generationAuthorsCompaniesText]
  );
  const generationMentioningMember = useMemo(
    () => parseMultilineOrCommaList(generationMentioningMemberText),
    [generationMentioningMemberText]
  );
  const generationMentioningCompany = useMemo(
    () => parseMultilineOrCommaList(generationMentioningCompanyText),
    [generationMentioningCompanyText]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const payload = {
      page: 'ai_engagement_protocol',
      updatedAt: new Date().toISOString(),
      oneClickChannel,
      linkedin: {
        keyword: generationKeyword.trim(),
        dateFilter: generationDateFilter,
        exactMatch: generationExactMatch,
        postCount: generationPostCount,
        specificPostUrls: linkedinSpecificPostUrls.length,
        authorJobTitle: generationAuthorJobTitle.trim(),
        authorLocation: generationAuthorLocation.trim(),
        industryUrn: generationIndustryUrn.trim(),
        companyPageSearch: {
          enabled: companyPageSearchEnabled,
          seniorities: compactStringList(companyPageSeniorities),
          departments: compactStringList(companyPageDepartments),
        },
      },
      facebook: {
        keyword: facebookKeyword.trim(),
        dateFilter: facebookDateFilter,
        postCount: facebookPostCount,
        specificPostUrls: facebookSpecificPostUrls.length,
        groupUrls: facebookGroupUrls.length,
      },
      trendFinder: {
        keywords: compactStringList(trendKeywordsText.split('\n')),
        dateFilter: trendDateFilter,
        exactMatch: trendExactMatch,
      },
      outcomes: {
        linkedin: generationOutcomes.length,
        facebook: facebookOutcomes.length,
      },
      stats: {
        linkedin: generationStats,
        facebook: facebookGenerationStats,
      },
    };
    try {
      const raw = window.localStorage.getItem(GUIDE_CONTEXT_STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : {};
      const next =
        existing && typeof existing === 'object'
          ? { ...(existing as Record<string, unknown>), aiEngagement: payload, updatedAt: new Date().toISOString() }
          : { aiEngagement: payload, updatedAt: new Date().toISOString() };
      window.localStorage.setItem(GUIDE_CONTEXT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Ignore non-critical guide context persistence errors.
    }
  }, [
    oneClickChannel,
    generationKeyword,
    generationDateFilter,
    generationExactMatch,
    generationPostCount,
    linkedinSpecificPostUrls.length,
    generationAuthorJobTitle,
    generationAuthorLocation,
    generationIndustryUrn,
    companyPageSearchEnabled,
    companyPageSeniorities,
    companyPageDepartments,
    facebookKeyword,
    facebookDateFilter,
    facebookPostCount,
    facebookSpecificPostUrls.length,
    facebookGroupUrls.length,
    trendKeywordsText,
    trendDateFilter,
    trendExactMatch,
    generationOutcomes.length,
    facebookOutcomes.length,
    generationStats,
    facebookGenerationStats,
  ]);
  const linkedinUnmatchedReasonSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const outcome of generationOutcomes) {
      if (outcome.searchResultId) continue;
      const reason = humanizeOutcomeReason(outcome.error || outcome.searchAttemptType || 'No search result');
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
  }, [generationOutcomes]);
  const linkedinResearchFailureCount = useMemo(
    () => generationOutcomes.filter((outcome) => Boolean(outcome.searchResultId) && !outcome.researched).length,
    [generationOutcomes]
  );
  const oneClickOutcomeRows = useMemo(
    () => [
      ...generationOutcomes
        .map((outcome) => ({ source: 'linkedin' as const, outcome })),
      ...facebookOutcomes
        .map((outcome) => ({ source: 'facebook' as const, outcome })),
    ],
    [generationOutcomes, facebookOutcomes]
  );
  const oneClickLeads = useMemo(() => {
    const rows: OneClickLeadRow[] = [];
    const dedupe = new Set<string>();
    for (const row of oneClickOutcomeRows) {
      const contactId = String(row.outcome.contactId ?? '').trim();
      const searchResultId = String(row.outcome.searchResultId ?? '').trim();
      const fromResearch =
        (searchResultId && oneClickContactsFromResearch[searchResultId]) ||
        null;
      const fromContacts =
        (contactId && oneClickContactsByKey[`contact:${contactId}`]) ||
        (searchResultId && oneClickContactsByKey[`search:${searchResultId}`]) ||
        null;
      const fallbackContact: OneClickContact = {
        searchResultId: searchResultId || undefined,
        contactId: contactId || undefined,
        fullName: row.outcome.name || undefined,
        company: row.outcome.company || undefined,
      };
      const contact = {
        ...fallbackContact,
        ...(fromResearch || {}),
        ...(fromContacts || {}),
      } as OneClickContact;
      const baseKey = contactId || searchResultId || row.outcome.profileUrl || `${row.source}-${row.outcome.name}`;
      const channels: OneClickChannel[] = ['linkedin', 'facebook', 'instagram', 'tiktok', 'substack', 'x'];
      for (const channel of channels) {
        const channelUrl = getContactSocialUrl(contact, channel);
        const sourceUrl =
          channel === row.source
            ? String(row.outcome.profileUrl ?? '').trim()
            : '';
        const profileUrl = channelUrl || sourceUrl;
        if (!profileUrl) continue;
        const dedupeKey = `${channel}:${baseKey}:${profileUrl.toLowerCase()}`;
        if (dedupe.has(dedupeKey)) continue;
        dedupe.add(dedupeKey);
        rows.push({
          channel,
          source: row.source,
          profileUrl,
          outcome: row.outcome,
          contact,
          contactKey: baseKey,
        });
      }
    }
    return rows;
  }, [oneClickOutcomeRows, oneClickContactsByKey, oneClickContactsFromResearch]);
  const filteredOneClickLeads = useMemo(
    () => oneClickLeads.filter((row) => row.channel === oneClickChannel),
    [oneClickLeads, oneClickChannel]
  );
  const refreshOneClickContacts = useCallback(async () => {
    await loadOneClickRecent();
  }, [loadOneClickRecent]);

  const scheduleOneClickRun = useCallback(
    async (channel: 'linkedin' | 'facebook') => {
      if (!userEmail) return;
      const keyword = (channel === 'linkedin' ? generationKeyword : facebookKeyword).trim();
      if (!keyword) {
        const message = 'Enter a keyword before scheduling a recurring run.';
        if (channel === 'linkedin') setGenerationError(message);
        else setFacebookGenerationError(message);
        return;
      }

      setOneClickScheduleLoading((prev) => ({ ...prev, [channel]: true }));
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
        const payload =
          channel === 'linkedin'
            ? {
                userEmail,
                name: `One Click LinkedIn · ${keyword}`,
                scheduleDays: [...ONE_CLICK_SCHEDULE_DAYS],
                scheduleHour: ONE_CLICK_SCHEDULE_HOUR,
                timezone,
                listenerConfig: {
                  platform: 'linkedin',
                  source: 'linkedin_listeners',
                  mode: 'keyword',
                  term: keyword,
                  date_filter: generationDateFilter || 'past-week',
                  sort_type: 'date_posted',
                  page_number: 1,
                  limit: 50,
                },
              }
            : {
                userEmail,
                name: `One Click Facebook · ${keyword}`,
                scheduleDays: [...ONE_CLICK_SCHEDULE_DAYS],
                scheduleHour: ONE_CLICK_SCHEDULE_HOUR,
                timezone,
                listenerConfig: {
                  platform: 'facebook',
                  source: 'facebook_listeners',
                  mode: 'keyword',
                  term: keyword,
                  searchType: facebookDateFilter === 'past-24h' ? 'latest' : 'top',
                  resultsCount: Math.max(1, Math.min(100, Number(facebookPostCount) || 25)),
                },
              };

        const res = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/social-listeners/schedule`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.success) throw new Error(json?.error || 'Failed to create schedule');

        const successMessage = `Scheduled ${channel} run (${ONE_CLICK_SCHEDULE_DAYS.join(', ')} @ ${String(
          ONE_CLICK_SCHEDULE_HOUR
        ).padStart(2, '0')}:00).`;
        if (channel === 'linkedin') {
          setGenerationStatus(successMessage);
          setGenerationError('');
        } else {
          setFacebookGenerationStatus(successMessage);
          setFacebookGenerationError('');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create schedule';
        if (channel === 'linkedin') setGenerationError(message);
        else setFacebookGenerationError(message);
      } finally {
        setOneClickScheduleLoading((prev) => ({ ...prev, [channel]: false }));
      }
    },
    [facebookDateFilter, facebookKeyword, facebookPostCount, generationDateFilter, generationKeyword, userEmail]
  );

  const applyLocalEngagement = useCallback((contact: OneClickContact, engagement: OneClickEngagement) => {
    const nextContact = { ...contact, pepai_engagement: engagement };
    const contactId = String(nextContact.contactId ?? nextContact.id ?? '').trim();
    const searchResultId = String(nextContact.searchResultId ?? '').trim();
    setOneClickContactsByKey((prev) => {
      const next = { ...prev };
      if (contactId) next[`contact:${contactId}`] = nextContact;
      if (searchResultId) next[`search:${searchResultId}`] = nextContact;
      return next;
    });
    if (searchResultId) {
      setOneClickContactsFromResearch((prev) => ({ ...prev, [searchResultId]: nextContact }));
    }
  }, []);

  const applyLocalSocialDataset = useCallback(
    (
      contact: OneClickContact,
      platform: ContactSocialPlatform,
      datasetId: string,
      kind: 'profile' | 'posts' = 'profile'
    ) => {
      const trimmed = String(datasetId ?? '').trim();
      if (!trimmed) return;
      const nextContact = { ...contact } as OneClickContact & Record<string, unknown>;
      const appendUnique = (field: string) => {
        const current = Array.isArray(nextContact[field]) ? (nextContact[field] as string[]) : [];
        nextContact[field] = Array.from(new Set([...current, trimmed]));
      };

      if (platform === 'linkedin') {
        appendUnique(kind === 'posts' ? 'linkedin_posts_ds' : 'linkedin_profile_ds');
      } else if (platform === 'facebook') {
        appendUnique('facebook_ds');
      } else if (platform === 'instagram') {
        appendUnique('instagram_ds');
      } else if (platform === 'tiktok') {
        appendUnique('tiktok_ds');
      } else if (platform === 'x') {
        appendUnique('x_ds');
      }

      const contactId = String(nextContact.contactId ?? nextContact.id ?? '').trim();
      const searchResultId = String(nextContact.searchResultId ?? '').trim();
      setOneClickContactsByKey((prev) => {
        const next = { ...prev };
        if (contactId) next[`contact:${contactId}`] = nextContact;
        if (searchResultId) next[`search:${searchResultId}`] = nextContact;
        return next;
      });
      if (searchResultId) {
        setOneClickContactsFromResearch((prev) => ({ ...prev, [searchResultId]: nextContact }));
      }
    },
    []
  );

  const updateEngagement = useCallback(
    async (
      row: OneClickLeadRow,
      key: keyof OneClickEngagement,
      mode: 'toggle' | 'set' = 'toggle'
    ) => {
      const contact = row.contact;
      const contactId = String(contact.contactId ?? contact.id ?? row.outcome.contactId ?? '').trim();
      const searchResultId = String(contact.searchResultId ?? row.outcome.searchResultId ?? '').trim();
      const saveKey = `${searchResultId || contactId || row.profileUrl}:${key}`;
      const current = getEngagementFromContact(contact);
      const hasValue = Boolean(current[key]);
      const nextValue = mode === 'set' ? (hasValue ? current[key] : new Date().toISOString()) : (hasValue ? undefined : new Date().toISOString());
      const nextEngagement: OneClickEngagement = {
        ...current,
        [key]: nextValue,
      };

      applyLocalEngagement(contact, nextEngagement);

      if (!userEmail || (!contactId && !searchResultId)) return;
      setEngagementSaving((prev) => ({ ...prev, [saveKey]: true }));
      try {
        const cleaned = Object.fromEntries(
          Object.entries(nextEngagement).filter(([, value]) => Boolean(value))
        );
        const res = await updateContactRecord({
          userEmail,
          contactId: contactId || undefined,
          searchResultId: searchResultId || undefined,
          updates: { pepai_engagement: cleaned },
        });
        if (!res?.success) throw new Error(res?.error || 'Failed to save engagement');
      } catch (err) {
        console.error('Failed to persist engagement:', err);
      } finally {
        setEngagementSaving((prev) => ({ ...prev, [saveKey]: false }));
      }
    },
    [applyLocalEngagement, userEmail]
  );

  const handleFindContactSocial = useCallback(async (contact: OneClickContact, platform: ContactSocialPlatform) => {
    if (!userEmail) return;
    const searchResultId = String(contact.searchResultId ?? '').trim();
    if (!searchResultId) return;
    const key = `${searchResultId}-${platform}`;
    setSocialLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          searchResultId,
          platform,
          name: getContactName(contact),
          company_name: getContactCompany(contact),
          ...(platform === 'linkedin' ? { postedLimit: '6months' } : {}),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Social profile search failed');
      await refreshOneClickContacts();
    } catch (err) {
      console.error('contact-socials/search failed', err);
    } finally {
      setSocialLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, [refreshOneClickContacts, userEmail]);

  const handleScrapeContactSocial = useCallback(async (contact: OneClickContact, platform: ContactSocialPlatform) => {
    if (!userEmail) return;
    const searchResultId = String(contact.searchResultId ?? '').trim();
    if (!searchResultId) return;
    const existing = getContactSocialDatasetIds(contact, platform);
    if (existing.profile || existing.posts) {
      setContactSocialDrawer({ contact, platform });
      return;
    }
    const url = getContactSocialUrl(contact, platform);
    if (!url) return;
    const key = `${searchResultId}-${platform}`;
    const jobId = `${platform}-${searchResultId}-${Date.now()}`;
    setSocialScrapeLoading((prev) => ({ ...prev, [key]: true }));
    addScrapeJob({
      id: jobId,
      label: `${platform.toUpperCase()} scrape · ${getContactName(contact)}`,
      source: platform,
      companyName: getContactCompany(contact),
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: 'Starting...',
    });
    try {
      const res = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          searchResultId,
          platform,
          url,
          ...(platform === 'linkedin' ? { postedLimit: '6months' } : {}),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Social scrape failed');
      updateScrapeJob(jobId, { status: 'running', progress: 15, message: 'Running...' });

      const pollStatus = async (runId: string, kind?: 'profile' | 'posts') => {
        for (let i = 0; i < 40; i += 1) {
          await sleep(3000);
          const statusRes = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/scrape/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userEmail,
              searchResultId,
              platform,
              run_id: runId,
              ...(kind ? { kind } : {}),
            }),
          });
          const statusData = await statusRes.json();
          if (!statusData?.success) throw new Error(statusData?.error || 'Scrape status failed');
          if (statusData.status === 'RUNNING') continue;
          if (statusData.status === 'FAILED') throw new Error('Social scrape failed');
          if (statusData.dataset_id) {
            applyLocalSocialDataset(contact, platform, String(statusData.dataset_id), kind === 'posts' ? 'posts' : 'profile');
          }
          return;
        }
        throw new Error('Social scrape status timeout');
      };

      if (platform === 'linkedin') {
        const profileRunId = String(data?.runs?.profile?.run_id ?? '').trim();
        const postsRunId = String(data?.runs?.posts?.run_id ?? '').trim();
        if (profileRunId) await pollStatus(profileRunId, 'profile');
        if (postsRunId) await pollStatus(postsRunId, 'posts');
      } else {
        const runId = String(data?.run_id ?? '').trim();
        if (runId) await pollStatus(runId);
      }

      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete' });
    } catch (err) {
      console.error('contact-socials/scrape failed', err);
      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed' });
    } finally {
      setSocialScrapeLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, [addScrapeJob, applyLocalSocialDataset, updateScrapeJob, userEmail]);

  const handleSaveContactSocial = useCallback(async () => {
    if (!userEmail || !socialEdit) return;
    const searchResultId = String(socialEdit.contact.searchResultId ?? '').trim();
    if (!searchResultId) return;
    const url = socialEditValue.trim();
    try {
      const res = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          searchResultId,
          platform: socialEdit.platform,
          url,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to save URL');
      await refreshOneClickContacts();
      setSocialEdit(null);
      setSocialEditValue('');
    } catch (err) {
      console.error('contact-socials/set failed', err);
    }
  }, [refreshOneClickContacts, socialEdit, socialEditValue, userEmail]);

  const handleViewContactSocialReport = useCallback(async (contact: OneClickContact, platform: ContactSocialPlatform) => {
    if (!userEmail) return;
    const ids = getContactSocialDatasetIds(contact, platform);
    if (!ids.profile && !ids.posts) return;
    const key = `${String(contact.searchResultId ?? '')}-${platform}`;
    setContactSocialReportLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const result: Record<string, unknown> = {};
      if (ids.profile) {
        const res = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: ids.profile }),
        });
        const data = await res.json();
        if (data?.success) result.profile = data.items;
      }
      if (ids.posts) {
        const res = await fetch(`${API_CONFIG.RESEARCH_API}/contact-socials/dataset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: ids.posts }),
        });
        const data = await res.json();
        if (data?.success) result.posts = data.items;
      }
      setContactSocialReport({
        contact,
        platform,
        items: [result],
      });
    } catch (err) {
      console.error('contact-socials/dataset failed', err);
    } finally {
      setContactSocialReportLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, [userEmail]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!userEmail) return;
      setLoading(true);
      setError('');
      setNotice('');
      try {
        const res = await getPepProfile(userEmail);
        if (!active) return;
        if (!res.success || !res.profile) {
          setError(res.error || 'Failed to load PEP profile');
          return;
        }
        setProfile({
          ...EMPTY_PROFILE,
          ...res.profile,
          approvalMode: res.profile.approvalMode === 'autopilot' ? 'autopilot' : 'manual',
        });
        setLocked(Boolean(res.locked));
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || 'Failed to load PEP profile');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [userEmail]);

  useEffect(() => {
    if (!canUsePepaiResearch) return;
    let cancelled = false;
    listInstantlyCampaignOptions({ userEmail })
      .then((data) => {
        if (cancelled) return;
        const campaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
        setInstantlyCampaigns(campaigns);
        setSelectedInstantlyCampaignId((existing) => {
          if (existing && campaigns.some((campaign) => campaign.id === existing)) return existing;
          return (
            (data.defaultCampaign?.id && campaigns.some((campaign) => campaign.id === data.defaultCampaign?.id)
              ? data.defaultCampaign.id
              : campaigns[0]?.id) || ''
          );
        });
      })
      .catch((error) => console.error('Failed to load Instantly campaigns', error));
    return () => {
      cancelled = true;
    };
  }, [canUsePepaiResearch, userEmail]);

  useEffect(() => {
    if (selectedInstantlyCampaignId) {
      safeStorageSet(PEPAI_CAMPAIGN_STORAGE_KEY, selectedInstantlyCampaignId);
    } else {
      safeStorageRemove(PEPAI_CAMPAIGN_STORAGE_KEY);
    }
  }, [selectedInstantlyCampaignId]);

  // @ts-ignore - kept for backward compat, new component handles save internally
  const _onSave = async () => {
    if (!userEmail || !canSave) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await savePepProfile(userEmail, profile);
      if (!res.success || !res.profile) {
        setError(res.error || 'Failed to save PEP profile');
        return;
      }
      setProfile({
        ...EMPTY_PROFILE,
        ...res.profile,
        approvalMode: res.profile.approvalMode === 'autopilot' ? 'autopilot' : 'manual',
      });
      setLocked(Boolean(res.locked));
      setNotice('Saved');
    } catch (err: any) {
      setError(err?.message || 'Failed to save PEP profile');
    } finally {
      setSaving(false);
    }
  };

  const runLinkedInGeneration = async () => {
    if (!userEmail) return;
    const manualPostUrls = Array.from(new Set(linkedinSpecificPostUrls));
    const term = toLinkedInSearchQuery(generationKeyword, generationExactMatch);
    if (!manualPostUrls.length && !term) {
      setGenerationError('Enter a keyword or add LinkedIn post URLs');
      return;
    }

    setGenerationLoading(true);
    setGenerationError('');
    setGenerationStatus('Starting LinkedIn post search…');
    setGenerationStats(emptyStats);
    setGenerationPosts([]);
    setGenerationProfiles([]);

    const jobId = `pepai-${Date.now()}`;
    const selectedDate = LINKEDIN_DATE_OPTIONS.find((option) => option.value === generationDateFilter) ?? LINKEDIN_DATE_OPTIONS[2];
    const selectedPostCount = LINKEDIN_POST_COUNT_OPTIONS.find((option) => option.requested === generationPostCount) ?? LINKEDIN_POST_COUNT_OPTIONS[0];
    addScrapeJob({
      id: jobId,
      label: 'P.E.P.ai LinkedIn Generation',
      source: 'pepai',
      companyName: term || 'LinkedIn specific links',
      message: manualPostUrls.length ? 'Scraping specific LinkedIn posts…' : 'Scraping posts…',
      status: 'running',
      progress: 0,
      queuedAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
    });

    try {
      const seenPostUrls = new Set<string>();
      let selectedPosts: LinkedInSelectedPost[];

      if (manualPostUrls.length) {
        selectedPosts = manualPostUrls
          .filter((url) => {
            if (seenPostUrls.has(url)) return false;
            seenPostUrls.add(url);
            return true;
          })
          .slice(0, selectedPostCount.requested)
          .map((postUrl) => ({ postUrl, commentCount: 0 }));
        setGenerationStatus(`Using ${selectedPosts.length} specific LinkedIn post link${selectedPosts.length === 1 ? '' : 's'} for deep scraping.`);
      } else {
        const startListenerRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            platform: 'linkedin',
            mode: 'keyword',
            term,
            searchQueries: [term],
            sort_type: selectedDate.sortType,
            sortBy: generationSortBy,
            page_number: generationStartPage,
            startPage: generationStartPage,
            scrapePages: generationScrapePages,
            date_filter: selectedDate.value,
            commentsPostedLimit: generationCommentsPostedLimit || undefined,
            postedLimit: generationPostedLimit || undefined,
            postedLimitDate: generationPostedLimitDate || undefined,
            contentType: generationContentType || 'all',
            maxComments: generationMaxComments,
            maxPosts: Math.max(generationMaxPosts, selectedPostCount.fetch),
            maxReactions: generationMaxReactions,
            profileScraperMode: generationProfileScraperMode || 'short',
            commentsProfileScraperMode: generationCommentsProfileScraperMode || 'short',
            reactionsProfileScraperMode: generationReactionsProfileScraperMode || 'short',
            scrapeComments: generationScrapeComments,
            scrapeReactions: generationScrapeReactions,
            postNestedComments: generationPostNestedComments,
            postNestedReactions: generationPostNestedReactions,
            authorKeywords: generationAuthorKeywords.trim() || undefined,
            authorUrls: generationAuthorUrls.length > 0 ? generationAuthorUrls : undefined,
            authorsCompanies: generationAuthorsCompanies.length > 0 ? generationAuthorsCompanies : undefined,
            authorsIndustryId:
              generationAuthorsIndustryIds.length > 0
                ? generationAuthorsIndustryIds
                : generationIndustryUrn
                  ? [generationIndustryUrn]
                  : undefined,
            mentioningMember: generationMentioningMember.length > 0 ? generationMentioningMember : undefined,
            mentioningCompany: generationMentioningCompany.length > 0 ? generationMentioningCompany : undefined,
            author_industry_urns: generationIndustryUrn || undefined,
            author_job_title: generationAuthorJobTitle.trim() || undefined,
            limit: selectedPostCount.fetch,
          }),
        });
        const startListener = await startListenerRes.json();
        if (!startListener?.success || !startListener?.run_id) {
          throw new Error(startListener?.error || 'Failed to start LinkedIn listener search');
        }

        const listenerResults = await pollRunResults(
          String(startListener.run_id),
          { source: 'social_listeners', keyword: term },
          (msg) => setGenerationStatus(`LinkedIn posts: ${msg}`),
        );

        const englishListenerResults = listenerResults
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .filter((row) => isEnglishLinkedInRow(row));
        const listenerRowsWithUrls = listenerResults
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .filter((row) => getPostUrl(row).includes('linkedin.com'));
        const englishRowsWithUrls = englishListenerResults.filter((row) => getPostUrl(row).includes('linkedin.com'));
        const effectiveRows =
          englishRowsWithUrls.length > 0
            ? englishRowsWithUrls
            : listenerRowsWithUrls.length > 0
              ? listenerRowsWithUrls
              : englishListenerResults;

        const rankedPosts = effectiveRows
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .map((row) => ({
            postUrl: getPostUrl(row),
            commentCount: extractCommentCount(row),
          }))
          .filter((row) => row.postUrl.includes('linkedin.com'))
          .sort((a, b) => b.commentCount - a.commentCount);

        const candidatePosts = effectiveRows
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .map(extractAuthorCandidate)
          .filter((row): row is LinkedInAuthorCandidate => Boolean(row));

        setGenerationStatus(
          englishRowsWithUrls.length > 0
            ? `LinkedIn posts: ${englishRowsWithUrls.length}/${listenerResults.length} English URL posts kept.`
            : listenerRowsWithUrls.length > 0
              ? `LinkedIn posts: using ${listenerRowsWithUrls.length} URL-bearing rows (English fallback disabled due missing URLs).`
              : englishListenerResults.length > 0
                ? `LinkedIn posts: ${englishListenerResults.length}/${listenerResults.length} English rows found, but none contained post URLs.`
                : `LinkedIn posts: no English metadata detected; using ${effectiveRows.length} URL-bearing rows as fallback.`
        );

        const uniqueAuthorUrls = Array.from(
          new Set(
            candidatePosts
              .map((row) => normalizeLinkedInPersonProfileUrl(row.linkedinUrl))
              .filter((url): url is string => Boolean(url))
          )
        );
        const scrapedAuthors = new Map<string, ScrapedAuthorProfile>();

        for (let idx = 0; idx < uniqueAuthorUrls.length; idx += LINKEDIN_AUTHOR_SCRAPE_BATCH_SIZE) {
          const batchUrls = uniqueAuthorUrls.slice(idx, idx + LINKEDIN_AUTHOR_SCRAPE_BATCH_SIZE);
          const batchStart = idx + 1;
          const batchEnd = idx + batchUrls.length;
          setGenerationStatus(`Scraping author profiles ${batchStart}-${batchEnd}/${uniqueAuthorUrls.length}`);
          const runId = await startAuthorProfileScrapeRun(userEmail, batchUrls);
          const authorResults = await pollRunResults(
            runId,
            { source: 'social_listeners', keyword: `${term}:authors:${batchStart}-${batchEnd}` },
          );
          for (const row of authorResults) {
            if (typeof row !== 'object' || row === null) continue;
            const profile = extractScrapedAuthorProfile(row as Record<string, unknown>);
            if (!profile) continue;
            scrapedAuthors.set(profile.linkedinUrl.toLowerCase(), profile);
          }
        }

        if (candidatePosts.length > 0 && uniqueAuthorUrls.length > 0) {
          setGenerationStats((prev) => ({ ...prev, authorProfilesFound: scrapedAuthors.size }));
          const scoredPosts = candidatePosts
            .map((post) => {
              const author = scrapedAuthors.get(post.linkedinUrl.toLowerCase());
              const locationScore = computeLocationScore(generationAuthorLocation, author?.location ?? '');
              return {
                ...post,
                authorLocation: author?.location ?? '',
                locationScore,
                score: scoreAuthorPost(generationAuthorLocation, author?.location ?? '', post.commentCount),
              };
            })
            .filter((post) => !generationAuthorLocation.trim() || post.locationScore > 0)
            .sort((a, b) => b.score - a.score || b.commentCount - a.commentCount);

          selectedPosts = scoredPosts
            .filter((row) => {
              if (!row.postUrl.includes('linkedin.com')) return false;
              if (seenPostUrls.has(row.postUrl)) return false;
              seenPostUrls.add(row.postUrl);
              return true;
            })
            .slice(0, selectedPostCount.requested)
            .map((row) => ({
              postUrl: row.postUrl,
              commentCount: row.commentCount,
              authorLinkedinUrl: row.linkedinUrl,
              authorName: row.name,
              authorCompany: row.company,
              authorTitle: row.title,
            }));
        } else {
          setGenerationStatus(`Author profiles unavailable from search results. Falling back to top ${selectedPostCount.requested} posts by comment count.`);
          selectedPosts = rankedPosts
            .filter((row) => {
              if (seenPostUrls.has(row.postUrl)) return false;
              seenPostUrls.add(row.postUrl);
              return true;
            })
            .slice(0, selectedPostCount.requested)
            .map((row) => ({ postUrl: row.postUrl, commentCount: row.commentCount }));
        }
      }

      const postUrls = selectedPosts.map((row) => row.postUrl);
      const linkedinTriggerPhrase =
        manualPostUrls.length > 0
          ? `Specific LinkedIn URLs (${manualPostUrls.length})`
          : term;

      if (!postUrls.length) {
        throw new Error('No LinkedIn post URLs found from listener results');
      }

      setGenerationStats((prev) => ({ ...prev, postsFound: postUrls.length }));
      setGenerationPosts(selectedPosts);
      setGenerationStatus(`Selected top ${postUrls.length} posts after author scoring from ${selectedPostCount.fetch} fetched results. Building profile queue…`);
      setGenerationCurrentPost(0);

      const profileSeen = new Set<string>();
      const queuedProfiles: Array<LinkedInCommentProfile & { postUrl?: string; commentText?: string }> = [];
      const pushQueuedProfile = (profile: LinkedInCommentProfile & { postUrl?: string; commentText?: string }) => {
        const linkedinUrl = normalizeLinkedInPersonProfileUrl(profile.linkedinUrl);
        if (!linkedinUrl) return false;
        const key = toProfileKey({ ...profile, linkedinUrl });
        if (profileSeen.has(key)) return false;
        profileSeen.add(key);
        queuedProfiles.push({ ...profile, linkedinUrl });
        return true;
      };

      for (const post of selectedPosts) {
        if (!post.authorLinkedinUrl) continue;
        pushQueuedProfile({
          name: String(post.authorName ?? '').trim(),
          company: String(post.authorCompany ?? '').trim(),
          title: String(post.authorTitle ?? '').trim(),
          linkedinUrl: post.authorLinkedinUrl,
          postUrl: post.postUrl,
          sourceType: 'post_author',
        });
      }

      for (let idx = 0; idx < postUrls.length; idx += LINKEDIN_DEEP_SCRAPE_BATCH_SIZE) {
        const batchUrls = postUrls.slice(idx, idx + LINKEDIN_DEEP_SCRAPE_BATCH_SIZE);
        const batchStart = idx + 1;
        const batchEnd = idx + batchUrls.length;
        setGenerationCurrentPost(batchEnd);
        setGenerationStatus(`Scraping posts ${batchStart}-${batchEnd}/${postUrls.length}`);
        const deepRunId = await startDeepScrapeRun(userEmail, batchUrls);
        const deepResults = await pollRunResults(
          deepRunId,
          { source: 'social_listeners', keyword: `${term || 'linkedin_urls'}:deep:${batchStart}-${batchEnd}` },
        );

        for (const row of deepResults) {
          const sourcePostUrl = getPostUrl(row) || batchUrls[0] || '';
          const authorProfile = extractAuthorProfile(row);
          if (authorProfile) {
            pushQueuedProfile({ ...authorProfile, postUrl: sourcePostUrl, sourceType: 'post_author' });
          }

          const commentCount = extractCommentCount(row);
          if (commentCount < 2) continue;
          const commentProfiles = extractLinkedInCommentProfiles([row], 500);
          for (const profile of commentProfiles) {
            pushQueuedProfile({ ...profile, postUrl: sourcePostUrl });
          }
        }
        setGenerationStats((prev) => ({ ...prev, postsProcessed: Math.min(postUrls.length, prev.postsProcessed + batchUrls.length) }));
      }

      if (!queuedProfiles.length) {
        throw new Error('No profiles found from scraped posts/comments');
      }

      setGenerationProfiles(queuedProfiles);

      const upsertOutcome = (patch: Partial<LinkedInGenerationOutcome> & { profileUrl: string }) => {
        setGenerationOutcomes((prev) => {
          const idx = prev.findIndex((item) => item.profileUrl === patch.profileUrl);
          const source = queuedProfiles.find((item) => item.linkedinUrl === patch.profileUrl);
          const base: LinkedInGenerationOutcome = {
            profileUrl: patch.profileUrl,
            name: source?.name || '',
            company: source?.company || '',
            triggerPhrase: linkedinTriggerPhrase,
            postUrl: source?.postUrl || '',
            commentText: source?.commentText || '',
            matched: false,
            researched: false,
          };
          if (idx === -1) return [...prev, { ...base, ...patch }];
          const next = [...prev];
          next[idx] = { ...next[idx], ...patch };
          return next;
        });
      };

      const researchCap = 500;
      let researched = 0;
      const resolveLeadBySearch = async (
        profileEntry: LinkedInCommentProfile & { postUrl?: string; commentText?: string }
      ): Promise<{ lead: Record<string, unknown>; attemptType: string } | null> => {
        const name = String(profileEntry.name ?? '').trim();
        const companyRaw = String(profileEntry.company ?? '').trim();
        const company = companyRaw === '-' || companyRaw === '—' ? '' : companyRaw;
        const titleRaw = String(profileEntry.title ?? '').trim();
        const title = titleRaw === '-' || titleRaw === '—' ? '' : titleRaw;
        const linkedin = extractLinkedInUrl(profileEntry.linkedinUrl);
        const attempts: Array<{ type: string; filters: Record<string, unknown>; perPage: number }> = [];
        if (name && company && title) {
          attempts.push({
            type: 'full_name_company_title',
            filters: { names: [name], companies: [company], titles: [title] },
            perPage: 10,
          });
        }
        if (name && company) {
          attempts.push({
            type: 'full_name_company',
            filters: { names: [name], companies: [company] },
            perPage: 10,
          });
        }
        if (name && linkedin) {
          attempts.push({
            type: 'full_name_linkedin_keyword',
            filters: { names: [name], keywords: [linkedin] },
            perPage: 10,
          });
        }
        for (const attempt of attempts) {
          const response = await fetch(`${API_CONFIG.SEARCH_API}/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userEmail,
              filters: attempt.filters,
              page: 0,
              perPage: attempt.perPage,
            }),
          });
          if (!response.ok) continue;
          const payload = await response.json().catch(() => ({}));
          const contacts = Array.isArray(payload?.contacts)
            ? (payload.contacts as Array<Record<string, unknown>>)
            : Array.isArray(payload?.data?.contacts)
              ? (payload.data.contacts as Array<Record<string, unknown>>)
              : [];
          const lead = contacts[0];
          if (lead && typeof lead === 'object') {
            return { lead, attemptType: attempt.type };
          }
        }
        return null;
      };

      const maxProcess = Math.min(researchCap, queuedProfiles.length);
      const processQueue = queuedProfiles.slice(0, maxProcess);

      for (let i = 0; i < processQueue.length; i += 1) {
        const item = processQueue[i];
        const profileUrl = String(item.linkedinUrl ?? '').trim();
        if (!profileUrl) continue;

        setGenerationStatus(`Running search/research ${i + 1}/${processQueue.length}`);
        updateScrapeJob(jobId, {
          message: `Searched ${i + 1} · Researched ${researched}`,
          progress: Math.min(99, Math.round(((i + 1) / Math.max(1, processQueue.length)) * 100)),
        });

        const resolved = await resolveLeadBySearch(item);
        if (!resolved) {
          upsertOutcome({
            profileUrl,
            name: String(item.name ?? '').trim(),
            company: String(item.company ?? '').trim(),
            postUrl: String(item.postUrl ?? '').trim(),
            commentText: String(item.commentText ?? '').trim(),
            matched: false,
            researched: false,
            error: 'no_search_result',
          });
          continue;
        }

        const lead = resolved.lead;
        const searchAttemptType = resolved.attemptType;
        const searchResultId = String(
          lead.searchResultId ??
          lead.SearchResultId ??
          lead.search_result_id ??
          ''
        ).trim();
        if (!searchResultId) {
          upsertOutcome({
            profileUrl,
            name: String(item.name ?? '').trim(),
            company: String(item.company ?? '').trim(),
            postUrl: String(item.postUrl ?? '').trim(),
            commentText: String(item.commentText ?? '').trim(),
            matched: false,
            researched: false,
            error: 'missing_real_search_result_id',
          });
          continue;
        }

        setGenerationStats((prev) => ({ ...prev, profilesSearched: i + 1, researched }));

        const queuePostUrl = String(item.postUrl ?? '').trim();
        const queueCommentText = String(item.commentText ?? '').trim();
        const companyName = String(lead.company ?? lead.companyName ?? lead.Company ?? item.company ?? '').trim();
        const leadTitle = String(lead.title ?? lead.Title ?? '').trim();
        const itemTitle = String(item.title ?? '').trim();
        const contactName = String(
          lead.name ??
          `${String(lead.firstName ?? '').trim()} ${String(lead.lastName ?? '').trim()}`.trim() ??
          item.name ??
          ''
        ).trim();
        const linkedin = String(
          lead.liUrl ?? lead.linkedInUrl ?? lead.linkedinUrl ?? lead.LIProfileUrl ?? profileUrl
        ).trim();
        const listenerTags = buildLinkedinListenerTags(searchAttemptType);
        const leadForPep: Record<string, unknown> = {
          ...lead,
          name: contactName,
          company: companyName,
          title: leadTitle || itemTitle,
          liUrl: linkedin,
          linkedinUrl: linkedin,
          searchResultId,
        };

        upsertOutcome({
          profileUrl,
          name: contactName || String(item.name ?? '').trim(),
          company: companyName || String(item.company ?? '').trim(),
          postUrl: queuePostUrl,
          commentText: queueCommentText,
          matched: true,
          researched: false,
          searchResultId,
          searchAttemptType,
        });

        let researchJson: Record<string, any> | null = null;
        try {
          researchJson = await researchContact(leadForPep as any, userEmail, {
            pepaiResearch: false,
            extraTags: listenerTags,
          });
        } catch (error) {
          upsertOutcome({
            profileUrl,
            matched: true,
            researched: false,
            error: error instanceof Error ? error.message : 'research_failed',
          });
          continue;
        }
        if (!researchJson?.success || !researchJson?.data) {
          upsertOutcome({
            profileUrl,
            matched: true,
            researched: false,
            error: String(researchJson?.error ?? 'research_failed').trim(),
          });
          continue;
        }

        researched += 1;
        setGenerationStats((prev) => ({ ...prev, researched }));
        const researchMeta =
          researchJson?.meta && typeof researchJson.meta === 'object'
            ? (researchJson.meta as Record<string, unknown>)
            : {};
        const contactIdFromMeta = String(researchMeta.contactId ?? '').trim();
        const researchData = toOneClickContactFromResearchData(researchJson?.data, {
          searchResultId,
          contactId: contactIdFromMeta || undefined,
        });
        setOneClickContactsFromResearch((prev) => ({ ...prev, [searchResultId]: researchData }));
        upsertOutcome({
          profileUrl,
          researched: true,
          contactId: contactIdFromMeta || undefined,
          companyEntityId: String(researchMeta.companyEntityId ?? '').trim() || undefined,
          writeDisposition: String(researchMeta.writeDisposition ?? '').trim() || undefined,
          error: undefined,
        });
      }
      updateScrapeJob(jobId, { status: 'succeeded', message: `Done · ${processQueue.length} searched · ${researched} researched`, progress: 100 });
      setGenerationStatus(`Completed. Job ${jobId} processed with research cap ${researchCap}; ${researched} researched.`);
    } catch (err: any) {
      setGenerationError(err?.message || 'LinkedIn generation failed');
      setGenerationStatus('');
    } finally {
      setGenerationLoading(false);
    }
  };

  const runLinkedinKeywordTrends = async () => {
    if (!userEmail) return;
    const keywords = Array.from(
      new Set(
        trendKeywordsText
          .split(/\n|,/)
          .map((value) => value.trim())
          .filter(Boolean)
      )
    ).slice(0, 20);
    if (!keywords.length) {
      setTrendError('Enter at least one keyword');
      return;
    }
    setTrendLoading(true);
    setTrendError('');
    setTrendRows([]);
    const rows: LinkedInTrendRow[] = [];
    try {
      for (let idx = 0; idx < keywords.length; idx += 1) {
        const keyword = toLinkedInSearchQuery(keywords[idx], trendExactMatch);
        const startRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            platform: 'linkedin',
            mode: 'keyword',
            term: keyword,
            sort_type: 'date_posted',
            page_number: 1,
            date_filter: trendDateFilter,
            limit: 50,
          }),
        });
        const startJson = await startRes.json();
        if (!startRes.ok || !startJson?.success || !startJson?.run_id) {
          rows.push({ keyword: keywords[idx], postsFound: 0 });
          continue;
        }
        const listenerResults = await pollRunResults(
          String(startJson.run_id),
          { source: 'social_listeners', keyword: `${keyword}:trend` },
        );
        const postsFound = listenerResults
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .filter((row) => String(getPostUrl(row)).includes('linkedin.com'))
          .filter((row) => isEnglishLinkedInRow(row))
          .length;
        const postUrls = Array.from(
          new Set(
            listenerResults
              .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
              .filter((row) => String(getPostUrl(row)).includes('linkedin.com'))
              .filter((row) => isEnglishLinkedInRow(row))
              .map((row) => getPostUrl(row))
              .filter(Boolean)
          )
        );
        rows.push({
          keyword: keywords[idx],
          postsFound,
          runId: String(startJson.run_id),
          datasetId: String(startJson.dataset_id ?? ''),
          postUrls,
        });
      }
      setTrendRows(rows.sort((a, b) => b.postsFound - a.postsFound));
    } catch (err: any) {
      setTrendError(err?.message || 'Failed to run LinkedIn keyword trend scan');
    } finally {
      setTrendLoading(false);
    }
  };

  const loadTrendUrlsIntoLinkedinGeneration = (row: LinkedInTrendRow) => {
    const urls = Array.isArray(row.postUrls) ? row.postUrls.filter(Boolean) : [];
    if (!urls.length) {
      setTrendError(`No LinkedIn post URLs found for "${row.keyword}"`);
      return;
    }
    setLinkedinSpecificPostUrlsText(urls.join('\n'));
    setGenerationKeyword(row.keyword);
    setLinkedinLeadGenTab('one_click_lead_gen');
    setGenerationStatus(`Loaded ${urls.length} LinkedIn post URLs from "${row.keyword}" into One-Click Lead Gen.`);
    setTrendError('');
  };

  const runFacebookGeneration = async () => {
    if (!userEmail) return;
    const term = facebookKeyword.trim();
    const manualPostUrls = Array.from(new Set(facebookSpecificPostUrls.map((url) => normalizeFacebookPostUrl(url)).filter((url) => isFacebookPostUrl(url))));
    if (!manualPostUrls.length && !term) {
      setFacebookGenerationError('Enter a keyword or add Facebook post URLs');
      return;
    }

    setFacebookGenerationLoading(true);
    setFacebookGenerationError('');
    setFacebookGenerationStatus('Starting Facebook post search…');
    setFacebookGenerationStats(emptyStats);
    setFacebookPosts([]);
    setFacebookProfiles([]);

    const jobId = `pepai-fb-${Date.now()}`;
    const selectedCampaign =
      canUsePepaiResearch && selectedInstantlyCampaignId
        ? instantlyCampaigns.find((campaign) => campaign.id === selectedInstantlyCampaignId) ?? null
        : null;
    const selectedDate = LINKEDIN_DATE_OPTIONS.find((option) => option.value === facebookDateFilter) ?? LINKEDIN_DATE_OPTIONS[2];
    const selectedPostCount = LINKEDIN_POST_COUNT_OPTIONS.find((option) => option.requested === facebookPostCount) ?? LINKEDIN_POST_COUNT_OPTIONS[0];
    addScrapeJob({
      id: jobId,
      label: 'P.E.P.ai Facebook Generation',
      source: 'pepai',
      companyName: term || 'Facebook specific links',
      message: manualPostUrls.length ? 'Scraping specific Facebook posts…' : 'Scraping posts…',
      status: 'running',
      progress: 0,
      queuedAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
    });

    try {
      let selectedPosts: FacebookSelectedPost[] = [];
      if (manualPostUrls.length) {
        selectedPosts = manualPostUrls.map((postUrl) => ({
          postUrl,
          commentCount: 0,
          authorFacebookUrl: '',
          authorName: '',
          authorCompany: '',
        }));
        setFacebookGenerationStatus(`Using ${selectedPosts.length} specific Facebook post link${selectedPosts.length === 1 ? '' : 's'} for scraping.`);
      } else {
        const startListenerRes = await fetch(`${API_CONFIG.ECOM_ORCHESTRATOR_API}/scrape/social-listeners`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            platform: 'facebook',
            mode: 'keyword',
            term,
            sort_type: selectedDate.sortType,
            page_number: 1,
            date_filter: selectedDate.value,
            limit: selectedPostCount.fetch,
          }),
        });
        const startListener = await startListenerRes.json();
        if (!startListener?.success || !startListener?.run_id) {
          throw new Error(startListener?.error || 'Failed to start Facebook listener search');
        }

        const listenerResults = await pollRunResults(
          String(startListener.run_id),
          { source: 'social_listeners', keyword: term },
          (msg) => setFacebookGenerationStatus(`Facebook posts: ${msg}`),
        );

        selectedPosts = listenerResults
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .map((row) => {
            const author = extractFacebookAuthorProfile(row);
            return {
              postUrl: getFacebookPostUrl(row),
              commentCount: extractCommentCount(row),
              authorFacebookUrl: author?.facebookUrl,
              authorName: author?.name,
              authorCompany: author?.company,
            };
          })
          .filter((row) => row.postUrl.includes('facebook.com'))
          .sort((a, b) => b.commentCount - a.commentCount)
          .slice(0, selectedPostCount.requested);
      }

      const postUrls = selectedPosts.map((row) => row.postUrl);
      const facebookTriggerPhrase =
        manualPostUrls.length > 0
          ? `Specific Facebook URLs (${manualPostUrls.length})`
          : term;
      if (!postUrls.length) {
        throw new Error('No Facebook post URLs available to scrape');
      }

      setFacebookGenerationStats((prev) => ({
        ...prev,
        postsFound: postUrls.length,
        authorProfilesFound: selectedPosts.filter((row) => Boolean(row.authorFacebookUrl)).length,
      }));
      setFacebookPosts(selectedPosts);
      setFacebookGenerationStatus(
        manualPostUrls.length
          ? `Queued ${postUrls.length} specific Facebook posts for deep scraping.`
          : `Selected top ${postUrls.length} Facebook posts. Building commenter queue…`
      );
      setFacebookGenerationCurrentPost(0);

      const profileIndexByKey = new Map<string, number>();
      const queuedProfiles: Array<FacebookCommentProfile & { postUrl?: string }> = [];
      const pushQueuedProfile = (profile: FacebookCommentProfile & { postUrl?: string }) => {
        const facebookUrl = extractFacebookUrl(profile.facebookUrl);
        if (!facebookUrl) return false;
        const key = getFacebookIdentityKey(facebookUrl);
        if (!key) return false;
        const nextProfile = { ...profile, facebookUrl };
        const existingIndex = profileIndexByKey.get(key);
        if (existingIndex !== undefined) {
          queuedProfiles[existingIndex] = chooseRicherFacebookProfile(queuedProfiles[existingIndex]!, nextProfile);
          return false;
        }
        profileIndexByKey.set(key, queuedProfiles.length);
        queuedProfiles.push(nextProfile);
        return true;
      };

      for (const post of selectedPosts) {
        if (!post.authorFacebookUrl) continue;
        pushQueuedProfile({
          name: String(post.authorName ?? '').trim(),
          company: String(post.authorCompany ?? '').trim(),
          facebookUrl: post.authorFacebookUrl,
          postUrl: post.postUrl,
          sourceType: 'post_author',
        });
      }

      for (let idx = 0; idx < postUrls.length; idx += 1) {
        const postUrl = postUrls[idx]!;
        const postNumber = idx + 1;
        setFacebookGenerationCurrentPost(postNumber);
        setFacebookGenerationStatus(`Scraping Facebook post ${postNumber}/${postUrls.length}`);
        const deepRunId = await startFacebookDeepScrapeRun(userEmail, [postUrl]);
        const deepResults = await pollRunResults(
          deepRunId,
          { source: 'social_listeners', keyword: `${term}:facebook:${postNumber}` },
          undefined,
          { maxAttempts: 120, pollIntervalMs: 3000, allowPartialResultsOnTimeout: true },
        );

        for (const row of deepResults) {
          const sourcePostUrl = getFacebookPostUrl(row) || postUrl;
          const authorProfile = extractFacebookAuthorProfile(row);
          if (authorProfile) {
            pushQueuedProfile({ ...authorProfile, postUrl: sourcePostUrl, sourceType: 'post_author' });
          }
          const commentProfiles = extractFacebookCommentProfiles([row], 500);
          for (const profile of commentProfiles) {
            pushQueuedProfile({ ...profile, postUrl: sourcePostUrl });
          }
        }
        setFacebookGenerationStats((prev) => ({ ...prev, postsProcessed: Math.min(postUrls.length, prev.postsProcessed + 1) }));
      }

      if (!queuedProfiles.length) {
        throw new Error('No Facebook profiles found from scraped posts/comments');
      }

      setFacebookProfiles(queuedProfiles);

      const upsertOutcome = (patch: Partial<FacebookGenerationOutcome> & { profileUrl: string }) => {
        setFacebookOutcomes((prev) => {
          const idx = prev.findIndex((item) => item.profileUrl === patch.profileUrl);
          const source = queuedProfiles.find((item) => item.facebookUrl === patch.profileUrl);
          const base: FacebookGenerationOutcome = {
            profileUrl: patch.profileUrl,
            name: source?.name || '',
            company: source?.company || '',
            triggerPhrase: facebookTriggerPhrase,
            postUrl: source?.postUrl || '',
            commentText: source?.commentText || '',
            matched: false,
            researched: false,
          };
          if (idx === -1) return [...prev, { ...base, ...patch }];
          const next = [...prev];
          next[idx] = { ...next[idx], ...patch };
          return next;
        });
      };

      const seedFacebookRes = await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          items: queuedProfiles.map((p) => ({
            profileUrl: p.facebookUrl,
            name: p.name,
            company: p.company,
            email: p.email || '',
            postUrl: p.postUrl || '',
            commentText: p.commentText || '',
            triggerPhrase: facebookTriggerPhrase,
          })),
        }),
      });
      const seedFacebookJson = await seedFacebookRes.json().catch(() => ({}));
      if (!seedFacebookRes.ok || seedFacebookJson?.success === false || !seedFacebookJson?.runId) {
        throw new Error(seedFacebookJson?.error || 'Failed to seed oneClickFacebook queue');
      }
      const facebookRunId = String(seedFacebookJson.runId);

      const researchCap = 500;
      let researched = 0;
      let sequencesQueued = 0;
      const pepCompanyProfiles = new Map<string, PepProfile | null>();
      const facebookProfileDetailsCache = new Map<string, FacebookProfileDetails | null>();
      const directQueue = queuedProfiles.slice(0, researchCap);

      for (let idx = 0; idx < directQueue.length; idx += 1) {
        const queued = directQueue[idx]!;
        const profileUrl = String(queued.facebookUrl ?? '').trim();
        const queuePostUrl = String(queued.postUrl ?? '').trim();
        const queueCommentText = String(queued.commentText ?? '').trim();
        let contactName = String(queued.name ?? '').trim();
        let companyName = String(queued.company ?? '').trim();
        let email = String(queued.email ?? '').trim();
        const searchResultId = `fb_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 8)}`;

        if (profileUrl) {
          if (!facebookProfileDetailsCache.has(profileUrl)) {
            try {
              setFacebookGenerationStatus(`Enriching Facebook profile ${idx + 1}/${directQueue.length}…`);
              facebookProfileDetailsCache.set(profileUrl, await enrichFacebookProfile(userEmail, profileUrl));
            } catch {
              facebookProfileDetailsCache.set(profileUrl, null);
            }
          }
          const enriched = facebookProfileDetailsCache.get(profileUrl);
          if (enriched) {
            contactName = contactName || enriched.name;
            companyName = companyName || enriched.company;
            email = email || enriched.email || extractFirstEmail(enriched.about);
          }
        }

        setFacebookGenerationStatus(`Creating contact from Facebook scrape ${idx + 1}/${directQueue.length}…`);
        updateScrapeJob(jobId, { message: `Created ${idx + 1} · Researched ${researched} · Sequences ${sequencesQueued}`, progress: Math.min(99, Math.round(((idx + 1) / Math.max(1, directQueue.length)) * 100)) });
        setFacebookGenerationStats((prev) => ({ ...prev, profilesSearched: idx + 1 }));

        upsertOutcome({
          runId: facebookRunId,
          profileUrl,
          name: contactName,
          company: companyName,
          postUrl: queuePostUrl,
          commentText: queueCommentText,
          matched: true,
          researched: false,
          searchResultId,
          searchAttemptType: 'facebook_scrape_direct',
        });

        if (profileUrl) {
          await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/mark`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              runId: facebookRunId,
              profileUrl,
              status: 'matched',
              matchedSearchResultId: searchResultId,
            }),
          });
        }

        const researchRes = await fetch(`${API_CONFIG.RESEARCH_API}/social-contacts/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyName,
            contactName,
            searchResultId,
            sourceSearchResultId: searchResultId,
            social: { linkedin: '', twitter: '', facebook: profileUrl, google: '' },
            title: '',
            uiMatchId: `facebook-direct-${idx + 1}`,
            naicsCode: '',
            naicsDesc: '',
            researchSource: 'P.E.P.ai Facebook Generation',
            researchedAt: new Date().toISOString(),
            userEmail,
            crmContext: {
              sourceType: 'pepai_facebook_generation',
              tags: ['PEPAI-research'],
            },
          }),
        });
        const researchJson = await researchRes.json();
        if (researchRes.ok && researchJson?.success !== false) {
          researched += 1;
          setFacebookGenerationStats((prev) => ({ ...prev, researched }));
          const researchMeta =
            researchJson?.meta && typeof researchJson.meta === 'object'
              ? (researchJson.meta as Record<string, unknown>)
              : {};
          const contactIdFromMeta = String(researchMeta.contactId ?? '').trim();
          const researchData = toOneClickContactFromResearchData(researchJson?.data, {
            searchResultId,
            contactId: contactIdFromMeta || undefined,
          });
          setOneClickContactsFromResearch((prev) => ({ ...prev, [searchResultId]: researchData }));
          upsertOutcome({
            runId: facebookRunId,
            profileUrl,
            researched: true,
            contactId: contactIdFromMeta || undefined,
            companyEntityId: String(researchMeta.companyEntityId ?? '').trim() || undefined,
            writeDisposition: String(researchMeta.writeDisposition ?? '').trim() || undefined,
            error: undefined,
          });

          if (profileUrl) {
            await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/mark`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                runId: facebookRunId,
                profileUrl,
                status: 'researched',
                matchedSearchResultId: searchResultId,
              }),
            });
          }

          const crmSyncStatus = String(researchJson?.crmSync?.status ?? '').trim().toUpperCase();
          if (!['SUCCESS', 'QUEUED'].includes(crmSyncStatus)) {
            const crmReason = String(researchJson?.crmSync?.reason ?? 'crm_sync_not_confirmed').trim() || 'crm_sync_not_confirmed';
            upsertOutcome({
              runId: facebookRunId,
              profileUrl,
              matched: true,
              researched: false,
              error: `crm_sync_failed: ${crmReason}`,
            });
            if (profileUrl) {
              await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/mark`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  runId: facebookRunId,
                  profileUrl,
                  status: 'failed',
                  matchedSearchResultId: searchResultId,
                  error: `crm_sync_failed: ${crmReason}`,
                }),
              });
            }
            continue;
          }

          let pepQueued = false;
          let pepQueueError = '';
          try {
            let pepCompanyProfile: PepProfile | null = null;
            if (companyName) {
              if (pepCompanyProfiles.has(companyName)) {
                pepCompanyProfile = pepCompanyProfiles.get(companyName) ?? null;
              } else {
                try {
                  const companyProfileRes = await getPepCompanyProfile(userEmail, companyName);
                  pepCompanyProfile = companyProfileRes?.success && companyProfileRes.profile ? companyProfileRes.profile : null;
                } catch {
                  pepCompanyProfile = null;
                }
                pepCompanyProfiles.set(companyName, pepCompanyProfile);
              }
            }

            const pepRequest = buildPepSequenceRequestFromResearchMatch({
              tenantId: userEmail,
              lead: {
                searchResultId,
                fullName: contactName,
                company: companyName,
                Email: email,
                email1: email,
                facebook_url: profileUrl,
                facebookUrl: profileUrl,
              },
              researched: (researchJson?.data && typeof researchJson.data === 'object') ? researchJson.data as Record<string, unknown> : null,
              pepProfile: profile,
              pepCompanyProfile,
              sourcePostUrl: queuePostUrl,
              matchedProfileUrl: profileUrl,
              matchedSocialProfileUrl: profileUrl,
              commentText: queueCommentText,
              senderName: 'Simply Scrapable',
              senderCompany: 'Simply Scrapable',
              instantlyCampaignId: selectedCampaign?.id,
              instantlyCampaignName: selectedCampaign?.name,
              programType: 'FACEBOOK_COMMENTER',
              entrySource: 'FACEBOOK_GENERATION',
              sourcePlatform: 'facebook',
            });

            const pepPayload = (await generatePepJob(pepRequest)) as Record<string, unknown>;
            const pepSuccess = pepPayload?.success;
            const pepError = String(pepPayload?.error ?? '').trim();
            if (pepSuccess === false || pepError) {
              throw new Error(pepError || 'pep_job_not_queued');
            }

            sequencesQueued += 1;
            setFacebookGenerationStats((prev) => ({ ...prev, sequencesQueued }));
            pepQueued = true;
          } catch (pepErr) {
            pepQueueError = pepErr instanceof Error ? pepErr.message : String(pepErr);
            console.error('Facebook PEPai sequence queue failed', {
              searchResultId,
              companyName,
              contactName,
              error: pepQueueError,
            });
          }

          if (!pepQueued) {
            upsertOutcome({
              runId: facebookRunId,
              profileUrl,
              matched: true,
              researched: false,
              error: `pep_queue_failed: ${pepQueueError || 'unknown_error'}`,
            });
            if (profileUrl) {
              await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/mark`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  runId: facebookRunId,
                  profileUrl,
                  status: 'failed',
                  matchedSearchResultId: searchResultId,
                  error: `pep_queue_failed: ${pepQueueError || 'unknown_error'}`,
                }),
              });
            }
            continue;
          }

          await new Promise((r) => setTimeout(r, 3000));
        } else {
          upsertOutcome({
            runId: facebookRunId,
            profileUrl,
            matched: true,
            researched: false,
            error: String(researchJson?.error ?? 'research_failed').trim(),
          });
          if (profileUrl) {
            await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/mark`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                runId: facebookRunId,
                profileUrl,
                status: 'failed',
                matchedSearchResultId: searchResultId,
                error: String(researchJson?.error ?? 'research_failed').trim(),
              }),
            });
          }
        }
      }

      updateScrapeJob(jobId, { status: 'succeeded', message: `Done · ${directQueue.length} created · ${researched} researched · ${sequencesQueued} sequences queued`, progress: 100 });
      setFacebookGenerationStatus(`Completed. Processed ${directQueue.length} Facebook scraped profiles; ${sequencesQueued} sequences queued.`);
    } catch (err: any) {
      setFacebookGenerationError(err?.message || 'Facebook generation failed');
      setFacebookGenerationStatus('');
    } finally {
      setFacebookGenerationLoading(false);
    }
  };

  const setFacebookCommentDraft = (key: string, value: string) => {
    setFacebookCommentDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const setLinkedinCommentDraft = (key: string, value: string) => {
    setLinkedinCommentDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const createLinkedinCommentTasks = async () => {
    if (!userEmail) return;
    setLinkedinCommentTaskLoading(true);
    setLinkedinCommentTaskError('');
    setLinkedinCommentTaskStatus('');
    try {
      const tasks = generationPosts.map((post) => ({
        postUrl: post.postUrl,
        targetUrl: post.postUrl,
        comment: (linkedinCommentDrafts[post.postUrl] ?? linkedinSharedComment).trim(),
        contactName: post.authorName ?? '',
        companyName: post.authorCompany ?? '',
        linkedinUrl: post.authorLinkedinUrl ?? '',
      }));

      const filteredTasks = tasks.filter((task) => String(task.targetUrl ?? '').trim() && String(task.comment ?? '').trim());
      if (!filteredTasks.length) {
        throw new Error('Add a shared comment or per-post comments before creating tasks.');
      }

      const res = await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-linkedin/comment-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          mode: linkedinCommentTaskMode,
          sharedComment: linkedinSharedComment.trim(),
          delaySeconds: linkedinCommentDelaySeconds,
          tasks: filteredTasks,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || json?.message || 'Failed to create LinkedIn comment tasks');
      }
      const taskCount = Number(json?.taskCount ?? filteredTasks.length);
      setLinkedinCommentTaskStatus(`Created ${taskCount} LinkedIn comment task${taskCount === 1 ? '' : 's'}. Always executed via proxy and may take 2-3 hours.`);
    } catch (err: any) {
      setLinkedinCommentTaskError(err?.message || 'Failed to create LinkedIn comment tasks');
    } finally {
      setLinkedinCommentTaskLoading(false);
    }
  };

  const createFacebookCommentTasks = async () => {
    if (!userEmail) return;
    setFacebookCommentTaskLoading(true);
    setFacebookCommentTaskError('');
    setFacebookCommentTaskStatus('');
    try {
      let tasks: Array<Record<string, unknown>> = [];
      if (facebookCommentTaskMode === 'specific_post_commenter') {
        const sourcePosts =
          facebookPosts.length > 0
            ? facebookPosts
            : facebookSpecificPostUrls.map((postUrl) => ({
                postUrl,
                commentCount: 0,
                authorFacebookUrl: '',
                authorName: '',
                authorCompany: '',
              }));
        tasks = sourcePosts.map((post) => ({
          postUrl: post.postUrl,
          targetUrl: post.postUrl,
          comment: (facebookCommentDrafts[post.postUrl] ?? facebookSharedComment).trim(),
          contactName: post.authorName ?? '',
          companyName: post.authorCompany ?? '',
          facebookUrl: post.authorFacebookUrl ?? '',
        }));
      } else {
        tasks = facebookGroupUrls.map((groupUrl) => ({
          targetUrl: groupUrl,
          comment:
            facebookCommentTaskMode === 'group_custom_comment'
              ? (facebookCommentDrafts[groupUrl] ?? '').trim()
              : facebookSharedComment.trim(),
        }));
      }

      const filteredTasks = tasks.filter((task) => String(task.targetUrl ?? '').trim() && String(task.comment ?? '').trim());
      if (!filteredTasks.length) {
        throw new Error(
          facebookCommentTaskMode === 'specific_post_commenter'
            ? 'Add a shared comment or per-post comments before creating tasks.'
            : 'Add valid group URLs and comments before creating tasks.'
        );
      }

      const res = await fetch(`${API_CONFIG.RESEARCH_API}/oneclick-facebook/comment-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          mode: facebookCommentTaskMode,
          sharedComment: facebookSharedComment.trim(),
          delaySeconds: facebookCommentDelaySeconds,
          tasks: filteredTasks,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || json?.message || 'Failed to create Facebook comment tasks');
      }
      const taskCount = Number(json?.taskCount ?? filteredTasks.length);
      setFacebookCommentTaskStatus(`Created ${taskCount} Facebook comment task${taskCount === 1 ? '' : 's'}. Always executed via proxy and may take 2-3 hours.`);
    } catch (err: any) {
      setFacebookCommentTaskError(err?.message || 'Failed to create Facebook comment tasks');
    } finally {
      setFacebookCommentTaskLoading(false);
    }
  };

  // setField moved to PepProfileEditorV2

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{isLinkedinListenerPage ? 'LinkedIn Listener' : 'P.E.P.ai'}</h1>
          <p className="text-gray-400 mt-2">
            {isLinkedinListenerPage
              ? 'One-click LinkedIn post, comment and profile scraping with results delivered to your CRM or sequence.'
              : 'Profile controls and one-click LinkedIn generation workflow.'}
          </p>
          {activeTab === 'profile' && locked && (
            <p className="text-amber-400 text-sm mt-2">
              This profile is managed centrally for `@profitibull.com` accounts.
            </p>
          )}
        </div>

        {isLinkedinListenerPage && (
          <div className="flex items-center gap-2 border-b border-border-dark pb-2">
            <button
              onClick={() => setLinkedinListenerPageTab('listener')}
              className={`px-3 py-1.5 rounded text-sm ${
                linkedinListenerPageTab === 'listener' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Listener
            </button>
            <button
              onClick={() => setLinkedinListenerPageTab('leads')}
              className={`px-3 py-1.5 rounded text-sm ${
                linkedinListenerPageTab === 'leads' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Leads
            </button>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="flex items-center gap-2 border-b border-border-dark pb-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-3 py-1.5 rounded text-sm ${
                activeTab === 'profile' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              P.E.P.ai Profile
            </button>
          </div>
        )}

        {(!isLinkedinListenerPage || linkedinListenerPageTab === 'leads') && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
                <h2 className="text-base font-semibold text-white">Social Media Lead Tables</h2>
              <p className="text-xs text-gray-400">
                Leads sourced from One Click runs, hydrated from One Click records, and available in channel drawers.
              </p>
            </div>
            <button
              onClick={() => {
                void refreshOneClickContacts();
              }}
              disabled={oneClickContactsLoading || !userEmail}
              className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs text-white"
            >
              {oneClickContactsLoading ? 'Refreshing…' : 'Refresh Leads'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {(['linkedin', 'facebook', 'instagram', 'tiktok', 'substack', 'x'] as OneClickChannel[]).map((channel) => {
              const count = oneClickLeads.filter((row) => row.channel === channel).length;
              return (
                <button
                  key={`oneclick-channel-${channel}`}
                  onClick={() => setOneClickChannel(channel)}
                  className={`px-3 py-1.5 rounded text-xs border ${
                    oneClickChannel === channel
                      ? 'bg-red-600 border-red-500 text-white'
                      : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {CHANNEL_LABELS[channel]} ({count})
                </button>
              );
            })}
          </div>

          {oneClickContactsError && <div className="text-xs text-red-400">{oneClickContactsError}</div>}

          <div className="overflow-auto max-h-[360px] rounded-lg border border-gray-800">
            <table className="w-full text-xs text-left">
              <thead className="text-gray-400 bg-gray-900/50">
                <tr>
                  <th className="px-2 py-2">Name/Username</th>
                  <th className="px-2 py-2">Company</th>
                  <th className="px-2 py-2">Social Profiles</th>
                  <th className="px-2 py-2">KW/Phrase</th>
                  <th className="px-2 py-2">Post URL</th>
                  <th className="px-2 py-2">Engagement</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOneClickLeads.length === 0 ? (
                  <tr className="border-t border-gray-800">
                    <td className="px-2 py-3 text-gray-500" colSpan={7}>
                      No {CHANNEL_LABELS[oneClickChannel]} leads from One Click outcomes yet.
                    </td>
                  </tr>
                ) : (
                  filteredOneClickLeads.map((row) => {
                    const name = getContactName(row.contact) || row.outcome.name || '-';
                    const company = getContactCompany(row.contact) || row.outcome.company || '-';
                    const outcomeStatus = getOneClickOutcomeStatus(row.outcome);
                    const platform = channelToSocialPlatform(row.channel);
                    const searchResultId = String(row.contact.searchResultId ?? '').trim();
                    const actionKey = `${searchResultId}-${platform ?? row.channel}`;
                    const engagement = getEngagementFromContact(row.contact);
                    const engagementKeyBase = searchResultId || String(row.contact.contactId ?? row.contact.id ?? '').trim() || row.profileUrl;
                    const engagementBusy = Object.entries(engagementSaving).some(([k, v]) => v && k.startsWith(`${engagementKeyBase}:`));
                    const triggerPhrase = String(row.outcome.triggerPhrase ?? '').trim();
                    const triggerDetail = String(row.outcome.searchAttemptType ?? '').trim();
                    const triggerText = [triggerPhrase, triggerDetail].filter(Boolean).join(' · ');
                    const socialLinks: Array<{
                      key: OneClickChannel;
                      url: string;
                      icon: JSX.Element;
                      title: string;
                    }> = (['linkedin', 'facebook', 'instagram', 'tiktok', 'substack', 'x'] as OneClickChannel[])
                      .map((channel) => {
                        const url = getContactSocialUrl(row.contact, channel);
                        const icon =
                          channel === 'linkedin' ? <Linkedin size={13} /> :
                          channel === 'facebook' ? <Facebook size={13} /> :
                          channel === 'instagram' ? <Instagram size={13} /> :
                          channel === 'tiktok' ? <Music2 size={13} /> :
                          channel === 'substack' ? <Newspaper size={13} /> :
                          <Twitter size={13} />;
                        return { key: channel, url, icon, title: CHANNEL_LABELS[channel] };
                      })
                      .filter((item) => Boolean(item.url));
                    return (
                      <tr key={`${row.channel}-${row.contactKey}-${row.profileUrl}`} className="border-t border-gray-800">
                        <td className="px-2 py-2 align-top text-white">{name}</td>
                        <td className="px-2 py-2 align-top text-white">{company}</td>
                        <td className="px-2 py-2 align-top">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ${
                                outcomeStatus === 'researched'
                                  ? 'border-emerald-500/50 text-emerald-300 bg-emerald-900/30'
                                  : outcomeStatus === 'matched'
                                    ? 'border-amber-500/50 text-amber-300 bg-amber-900/30'
                                    : outcomeStatus === 'failed'
                                      ? 'border-rose-500/50 text-rose-300 bg-rose-900/30'
                                      : 'border-gray-600 text-gray-300 bg-gray-900/30'
                              }`}
                              title={row.outcome.error ? `Error: ${row.outcome.error}` : undefined}
                            >
                              {outcomeStatus}
                            </span>
                            {socialLinks.length ? socialLinks.map((item) => (
                              <a
                                key={`${row.contactKey}-${item.key}`}
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                title={item.title}
                                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-700 text-gray-200 hover:bg-gray-800 hover:text-white"
                              >
                                {item.icon}
                              </a>
                            )) : (
                              <span className="text-[11px] text-gray-500">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-top text-gray-200">
                          {triggerText || '-'}
                        </td>
                        <td className="px-2 py-2 align-top">
                          {row.outcome.postUrl ? (
                            <a
                              href={row.outcome.postUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center px-2 py-1 rounded bg-sky-700 hover:bg-sky-600 text-white"
                            >
                              POST
                            </a>
                          ) : (
                            <span className="text-[11px] text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-2 py-2 align-top">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              title={engagement.viewedAt ? `Viewed ${formatEngagementTime(engagement.viewedAt)}` : 'Mark viewed'}
                              onClick={() => void updateEngagement(row, 'viewedAt')}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded border ${
                                engagement.viewedAt ? 'border-sky-500 text-sky-300 bg-sky-900/30' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                              }`}
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              type="button"
                              title={engagement.connectedAt ? `Connected ${formatEngagementTime(engagement.connectedAt)}` : 'Mark connected/friended'}
                              onClick={() => void updateEngagement(row, 'connectedAt')}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded border ${
                                engagement.connectedAt ? 'border-emerald-500 text-emerald-300 bg-emerald-900/30' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                              }`}
                            >
                              <UserPlus size={14} />
                            </button>
                            <button
                              type="button"
                              title={engagement.followedAt ? `Followed ${formatEngagementTime(engagement.followedAt)}` : 'Mark followed'}
                              onClick={() => void updateEngagement(row, 'followedAt')}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded border ${
                                engagement.followedAt ? 'border-indigo-500 text-indigo-300 bg-indigo-900/30' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                              }`}
                            >
                              <BellPlus size={14} />
                            </button>
                            <button
                              type="button"
                              title={engagement.commentedAt ? `Commented ${formatEngagementTime(engagement.commentedAt)}` : 'Mark commented on post'}
                              onClick={() => void updateEngagement(row, 'commentedAt')}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded border ${
                                engagement.commentedAt ? 'border-amber-500 text-amber-300 bg-amber-900/30' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                              }`}
                            >
                              <Megaphone size={14} />
                            </button>
                            <button
                              type="button"
                              title={engagement.messagedAt ? `DM/IM ${formatEngagementTime(engagement.messagedAt)}` : 'Mark DM/IM sent'}
                              onClick={() => void updateEngagement(row, 'messagedAt')}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded border ${
                                engagement.messagedAt ? 'border-rose-500 text-rose-300 bg-rose-900/30' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                              }`}
                            >
                              <MessageCircle size={14} />
                            </button>
                          </div>
                          {engagementBusy ? <div className="mt-1 text-[10px] text-gray-500">Saving…</div> : null}
                        </td>
                        <td className="px-2 py-2 align-top">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => {
                                setContactProfileDrawer({ contact: row.contact });
                                void updateEngagement(row, 'viewedAt', 'set');
                              }}
                              className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white"
                            >
                              Profile
                            </button>
                            {platform ? (
                              <button
                                onClick={() => {
                                  setContactSocialDrawer({ contact: row.contact, platform });
                                  void updateEngagement(row, 'viewedAt', 'set');
                                }}
                                className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white"
                              >
                                Drawer
                              </button>
                            ) : null}
                            {platform && !searchResultId ? (
                              <span className="text-[10px] text-amber-300">missing searchResultId</span>
                            ) : null}
                            {platform && searchResultId ? (
                              <button
                                onClick={() => handleScrapeContactSocial(row.contact, platform)}
                                disabled={!!socialScrapeLoading[actionKey]}
                                className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white"
                              >
                                {socialScrapeLoading[actionKey] ? 'Scraping…' : 'Scrape'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <PepProfileEditorV2 userEmail={userEmail} /> 
        )}

        {activeTab === 'linkedin_generation' && (!isLinkedinListenerPage || linkedinListenerPageTab === 'listener') && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold">LinkedIn Lead Generation</h2>
              <p className="text-gray-400 text-sm mt-1">
                One click workflow: LinkedIn post search → scrape profiles from posts and engagement (50) → run search/research for discovered profiles (50 max) → CRM tag `PEPAI-research`.
              </p>
              <p className="text-gray-500 text-xs mt-1">English-only post filter is enforced before scoring and profile queueing.</p>
            </div>

            <div className="flex items-center gap-2 border-b border-border-dark pb-2">
              <button
                onClick={() => setLinkedinLeadGenTab('keyword_trend_finder')}
                className={`px-3 py-1.5 rounded text-sm ${
                  linkedinLeadGenTab === 'keyword_trend_finder' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Keyword Trend Finder
              </button>
              <button
                onClick={() => setLinkedinLeadGenTab('one_click_lead_gen')}
                className={`px-3 py-1.5 rounded text-sm ${
                  linkedinLeadGenTab === 'one_click_lead_gen' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                One-Click Lead Gen
              </button>
            </div>

            {linkedinLeadGenTab === 'keyword_trend_finder' && (
            <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-4 space-y-3">
              <div className="text-sm font-semibold text-white">LinkedIn Keyword Trend Finder</div>
              <p className="text-xs text-gray-400">
                Finds English post volume by keyword for LinkedIn so users can pick stronger search terms.
              </p>
              <div className="rounded-lg border border-gray-700 bg-gray-900/30 px-3 py-2 text-xs text-gray-300">
                LinkedIn defaults to fuzzy/semantic matching. Turn on <span className="text-white font-medium">Exact phrase mode</span> to force exact words.
                Example: <span className="text-white">Hiring OR Growth</span> (fuzzy) vs <span className="text-white">"Hiring" OR "Growth"</span> (exact).
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px_auto] gap-3 items-end">
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Keywords (one per line)</div>
                  <textarea
                    value={trendKeywordsText}
                    onChange={(e) => setTrendKeywordsText(e.target.value)}
                    rows={4}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder={'saas leads\nsaas sales'}
                  />
                </label>
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Date Range</div>
                  <select
                    value={trendDateFilter}
                    onChange={(e) => setTrendDateFilter(e.target.value)}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {LINKEDIN_DATE_OPTIONS.filter((option) => option.value).map((option) => (
                      <option key={`trend-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={trendExactMatch}
                    onChange={(e) => setTrendExactMatch(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Exact phrase mode
                </label>
                <button
                  onClick={runLinkedinKeywordTrends}
                  disabled={trendLoading || !userEmail}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {trendLoading ? 'Scanning…' : 'Run Trend Finder'}
                </button>
              </div>
              {trendError && <div className="text-sm text-red-400">{trendError}</div>}
              {trendRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/70 text-gray-300">
                      <tr>
                        <th className="text-left px-3 py-2">Keyword</th>
                        <th className="text-left px-3 py-2">English Posts</th>
                        <th className="text-left px-3 py-2">Post URLs</th>
                        <th className="text-left px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendRows.map((row) => (
                        <tr key={row.keyword} className="border-t border-gray-800">
                          <td className="px-3 py-2 text-white">{row.keyword}</td>
                          <td className="px-3 py-2 text-sky-300 font-semibold">{row.postsFound}</td>
                          <td className="px-3 py-2 align-top">
                            {row.postUrls && row.postUrls.length > 0 ? (
                              <div className="space-y-1 max-w-[520px] max-h-56 overflow-y-auto pr-1">
                                {row.postUrls.map((url) => (
                                  <div key={`${row.keyword}-${url}`} className="truncate">
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-300 hover:text-blue-200 underline text-xs"
                                      title={url}
                                    >
                                      {url}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">No URLs</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => loadTrendUrlsIntoLinkedinGeneration(row)}
                              disabled={!row.postUrls || row.postUrls.length === 0}
                              className="px-2 py-1 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium"
                            >
                              Load URLs
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            )}

            {linkedinLeadGenTab === 'one_click_lead_gen' && (
            <>
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-700 bg-gray-900/30 px-3 py-2 text-xs text-gray-300">
                LinkedIn keyword search is fuzzy by default. Enable <span className="text-white font-medium">Exact phrase mode</span> to quote each term automatically.
                Example: <span className="text-white">Hiring OR Growth</span> becomes <span className="text-white">"Hiring" OR "Growth"</span>.
              </div>
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Keyword / Query</div>
                <input
                  value={generationKeyword}
                  onChange={(e) => setGenerationKeyword(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="lead generation"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Specific LinkedIn Post URLs (optional)</div>
                <textarea
                  value={linkedinSpecificPostUrlsText}
                  onChange={(e) => setLinkedinSpecificPostUrlsText(e.target.value)}
                  rows={3}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder={'Optional. Paste one LinkedIn post URL per line.\nhttps://www.linkedin.com/feed/update/urn:li:activity:...\nhttps://www.linkedin.com/posts/...'}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  If post URLs are provided, One-Click Lead Gen uses URL scraping directly and does not rely on keyword discovery.
                </p>
              </label>
              <div className="rounded-lg border border-gray-700 bg-gray-900/30 p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={companyPageSearchEnabled}
                    onChange={(e) => setCompanyPageSearchEnabled(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Company Page Search
                </label>
                {companyPageSearchEnabled && (
                  <>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Seniority To Scrape</div>
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={companyPageSeniorities.length === COMPANY_PAGE_SENIORITY_OPTIONS.length}
                            onChange={(e) =>
                              setCompanyPageSeniorities(e.target.checked ? [...COMPANY_PAGE_SENIORITY_OPTIONS] : [])
                            }
                            className="h-4 w-4"
                          />
                          All of the above
                        </label>
                        {COMPANY_PAGE_SENIORITY_OPTIONS.map((option) => (
                          <label key={`company-seniority-${option}`} className="flex items-center gap-2 text-sm text-gray-300">
                            <input
                              type="checkbox"
                              checked={companyPageSeniorities.includes(option)}
                              onChange={(e) =>
                                setCompanyPageSeniorities((prev) =>
                                  e.target.checked
                                    ? Array.from(new Set([...prev, option]))
                                    : prev.filter((value) => value !== option)
                                )
                              }
                              className="h-4 w-4"
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Departments</div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {DEPARTMENT_OPTIONS.map((option) => (
                          <label key={`company-dept-${option}`} className="flex items-center gap-2 text-sm text-gray-300">
                            <input
                              type="checkbox"
                              checked={companyPageDepartments.includes(option)}
                              onChange={(e) =>
                                setCompanyPageDepartments((prev) =>
                                  e.target.checked
                                    ? Array.from(new Set([...prev, option]))
                                    : prev.filter((value) => value !== option)
                                )
                              }
                              className="h-4 w-4"
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Posts To Process</div>
                <select
                  value={generationPostCount}
                  onChange={(e) => setGenerationPostCount(Number(e.target.value))}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                >
                  {LINKEDIN_POST_COUNT_OPTIONS.map((option) => (
                    <option key={option.requested} value={option.requested}>
                      {option.requested}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Date Range</div>
                <select
                  value={generationDateFilter}
                  onChange={(e) => setGenerationDateFilter(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                >
                  {LINKEDIN_DATE_OPTIONS.map((option) => (
                    <option key={option.value || 'any'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Author Industry</div>
                <select
                  value={generationIndustryUrn}
                  onChange={(e) => setGenerationIndustryUrn(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="">All industries</option>
                  {linkedInIndustryGroups.map((group) => (
                    <optgroup key={group.group} label={group.group}>
                      {group.options.map((option) => (
                        <option key={`${group.group}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/30 p-3 space-y-3">
                <div className="text-xs uppercase tracking-wide text-gray-400">Advanced LinkedIn Search Options</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Comments Posted Limit</div>
                    <select
                      value={generationCommentsPostedLimit}
                      onChange={(e) => setGenerationCommentsPostedLimit(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_POSTED_LIMIT_OPTIONS.map((option) => (
                        <option key={`comments-limit-${option.value || 'any'}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Posted Limit</div>
                    <select
                      value={generationPostedLimit}
                      onChange={(e) => setGenerationPostedLimit(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_POSTED_LIMIT_OPTIONS.map((option) => (
                        <option key={`posted-limit-${option.value || 'any'}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Posted Limit Date</div>
                    <input
                      type="date"
                      value={generationPostedLimitDate}
                      onChange={(e) => setGenerationPostedLimitDate(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Start Page</div>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      value={generationStartPage}
                      onChange={(e) => setGenerationStartPage(Math.max(1, Number(e.target.value || 1)))}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Scrape Pages</div>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={generationScrapePages}
                      onChange={(e) => setGenerationScrapePages(Math.max(1, Number(e.target.value || 1)))}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Sort By</div>
                    <select
                      value={generationSortBy}
                      onChange={(e) => setGenerationSortBy(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_SORT_BY_OPTIONS.map((option) => (
                        <option key={`sort-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Content Type</div>
                    <select
                      value={generationContentType}
                      onChange={(e) => setGenerationContentType(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_CONTENT_TYPE_OPTIONS.map((option) => (
                        <option key={`content-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Author Keywords</div>
                    <input
                      value={generationAuthorKeywords}
                      onChange={(e) => setGenerationAuthorKeywords(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                      placeholder="saas"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Max Comments</div>
                    <input
                      type="number"
                      min={0}
                      max={500}
                      value={generationMaxComments}
                      onChange={(e) => setGenerationMaxComments(Math.max(0, Number(e.target.value || 0)))}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Max Posts</div>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={generationMaxPosts}
                      onChange={(e) => setGenerationMaxPosts(Math.max(1, Number(e.target.value || 1)))}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Max Reactions</div>
                    <input
                      type="number"
                      min={0}
                      max={500}
                      value={generationMaxReactions}
                      onChange={(e) => setGenerationMaxReactions(Math.max(0, Number(e.target.value || 0)))}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Profile Scraper Mode</div>
                    <select
                      value={generationProfileScraperMode}
                      onChange={(e) => setGenerationProfileScraperMode(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_PROFILE_MODE_OPTIONS.map((option) => (
                        <option key={`profile-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Comments Profile Mode</div>
                    <select
                      value={generationCommentsProfileScraperMode}
                      onChange={(e) => setGenerationCommentsProfileScraperMode(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_PROFILE_MODE_OPTIONS.map((option) => (
                        <option key={`comments-profile-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Reactions Profile Mode</div>
                    <select
                      value={generationReactionsProfileScraperMode}
                      onChange={(e) => setGenerationReactionsProfileScraperMode(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {LINKEDIN_PROFILE_MODE_OPTIONS.map((option) => (
                        <option key={`reactions-profile-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Author URLs (one per line)</div>
                    <textarea
                      value={generationAuthorUrlsText}
                      onChange={(e) => setGenerationAuthorUrlsText(e.target.value)}
                      rows={3}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                      placeholder={'https://www.linkedin.com/in/example\nlinkedin.com/in/another'}
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Authors Companies (one per line)</div>
                    <textarea
                      value={generationAuthorsCompaniesText}
                      onChange={(e) => setGenerationAuthorsCompaniesText(e.target.value)}
                      rows={3}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                      placeholder={'Nike\nHubSpot'}
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Authors Industry (friendly name → ID)</div>
                    <select
                      multiple
                      value={generationAuthorsIndustryIds}
                      onChange={(e) =>
                        setGenerationAuthorsIndustryIds(
                          Array.from(e.target.selectedOptions).map((option) => option.value)
                        )
                      }
                      className="w-full h-40 bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {linkedInIndustryGroups.map((group) => (
                        <optgroup key={`authors-industry-${group.group}`} label={group.group}>
                          {group.options.map((option) => (
                            <option key={`authors-industry-${group.group}-${option.value}`} value={option.value}>
                              {option.label} ({option.value})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <div className="mt-1 text-[11px] text-gray-400">
                      Selected: {generationAuthorsIndustryIds.length}
                    </div>
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Mentioning Member (one per line)</div>
                    <textarea
                      value={generationMentioningMemberText}
                      onChange={(e) => setGenerationMentioningMemberText(e.target.value)}
                      rows={3}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                      placeholder={'Bezos\nZuckerberg'}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Mentioning Company (one per line)</div>
                    <textarea
                      value={generationMentioningCompanyText}
                      onChange={(e) => setGenerationMentioningCompanyText(e.target.value)}
                      rows={3}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                      placeholder={'Nike\nSalesforce'}
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-2 content-start">
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={generationScrapeComments}
                        onChange={(e) => setGenerationScrapeComments(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Scrape comments
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={generationScrapeReactions}
                        onChange={(e) => setGenerationScrapeReactions(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Scrape reactions
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={generationPostNestedComments}
                        onChange={(e) => setGenerationPostNestedComments(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Nested comments
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={generationPostNestedReactions}
                        onChange={(e) => setGenerationPostNestedReactions(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Nested reactions
                    </label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,220px)_minmax(0,220px)_auto] gap-3 items-end">
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Author Job Title</div>
                <input
                  value={generationAuthorJobTitle}
                  onChange={(e) => setGenerationAuthorJobTitle(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="owner"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Author Location</div>
                <input
                  value={generationAuthorLocation}
                  onChange={(e) => setGenerationAuthorLocation(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="United States"
                />
              </label>
              {canUsePepaiResearch && instantlyCampaigns.length > 0 ? (
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Email Campaign</div>
                  <select
                    value={selectedInstantlyCampaignId}
                    onChange={(e) => setSelectedInstantlyCampaignId(e.target.value)}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {instantlyCampaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={generationExactMatch}
                  onChange={(e) => setGenerationExactMatch(e.target.checked)}
                  className="h-4 w-4"
                />
                Exact phrase mode
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={runLinkedInGeneration}
                  disabled={generationLoading || !userEmail}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {generationLoading ? 'Running…' : 'Run LinkedIn Generation'}
                </button>
                <button
                  onClick={() => void scheduleOneClickRun('linkedin')}
                  disabled={oneClickScheduleLoading.linkedin || !userEmail}
                  className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {oneClickScheduleLoading.linkedin ? 'Scheduling…' : 'Schedule LinkedIn Run'}
                </button>
              </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Posts Found</div>
                <div className="text-white font-semibold">{generationStats.postsFound}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Posts Processed</div>
                <div className="text-white font-semibold">{generationStats.postsProcessed}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Author Profiles</div>
                <div className="text-white font-semibold">{generationStats.authorProfilesFound}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Profiles Searched</div>
                <div className="text-white font-semibold">{generationStats.profilesSearched}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Researched</div>
                <div className="text-white font-semibold">{generationStats.researched}</div>
              </div>
            </div>

            {generationStatus && <div className="text-sm text-sky-300">{generationStatus}</div>}
            {generationLoading && generationStats.postsFound > 0 && (
              <div className="text-xs text-gray-400">Current post: {generationCurrentPost}/{generationStats.postsFound}</div>
            )}
            {generationError && <div className="text-sm text-red-400">{generationError}</div>}

            {generationPosts.length > 0 && (
              <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Selected Posts</div>
                <div className="overflow-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-400">
                      <tr>
                        <th className="px-2 py-2">Post</th>
                        <th className="px-2 py-2">Comments</th>
                        <th className="px-2 py-2">Author</th>
                        <th className="px-2 py-2">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generationPosts.map((post) => (
                        <tr key={post.postUrl} className="border-t border-gray-800">
                          <td className="px-2 py-2 align-top break-all">
                            <a href={post.postUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                              {post.postUrl}
                            </a>
                          </td>
                          <td className="px-2 py-2 align-top text-white">{post.commentCount}</td>
                          <td className="px-2 py-2 align-top text-white">{post.authorName || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{post.authorCompany || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-4">
              <div>
                <div className="text-sm font-semibold text-white">LinkedIn Comment Tasks</div>
                <p className="text-xs text-amber-300 mt-1">
                  Comment actions are always executed via proxy. In many cases completion takes 2-3 hours. This uses the selected LinkedIn posts from the generation run.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <label className="block md:col-span-2">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Mode</div>
                  <select
                    value={linkedinCommentTaskMode}
                    onChange={(e) => {
                      setLinkedinCommentTaskMode(e.target.value as LinkedInCommentTaskMode);
                      setLinkedinCommentTaskStatus('');
                      setLinkedinCommentTaskError('');
                    }}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {LINKEDIN_COMMENT_TASK_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-400 mt-2">
                    {LINKEDIN_COMMENT_TASK_OPTIONS.find((option) => option.value === linkedinCommentTaskMode)?.help}
                  </div>
                </label>
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Delay (seconds)</div>
                  <input
                    type="number"
                    min={5}
                    value={linkedinCommentDelaySeconds}
                    onChange={(e) => setLinkedinCommentDelaySeconds(Math.max(5, Number(e.target.value) || 5))}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  />
                </label>
              </div>

              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Shared Comment Fallback</div>
                <textarea
                  value={linkedinSharedComment}
                  onChange={(e) => setLinkedinSharedComment(e.target.value)}
                  className="w-full min-h-[110px] bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="Optional shared comment to use unless a post has its own custom comment"
                />
              </label>

              {generationPosts.length > 0 && (
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wide text-gray-400">Per-Post Comments</div>
                  <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                    {generationPosts.map((post) => (
                      <div key={`linkedin-comment-post-${post.postUrl}`} className="rounded-lg border border-gray-800 bg-gray-900/30 p-3 space-y-2">
                        <div className="text-xs text-sky-300 break-all">{post.postUrl}</div>
                        <textarea
                          value={linkedinCommentDrafts[post.postUrl] ?? ''}
                          onChange={(e) => setLinkedinCommentDraft(post.postUrl, e.target.value)}
                          className="w-full min-h-[84px] bg-gray-950/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                          placeholder="Optional custom comment for this post"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={createLinkedinCommentTasks}
                  disabled={linkedinCommentTaskLoading || !userEmail || generationPosts.length === 0}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {linkedinCommentTaskLoading ? 'Creating Tasks…' : 'Create Comment Tasks'}
                </button>
                {linkedinCommentTaskStatus && <div className="text-sm text-green-300">{linkedinCommentTaskStatus}</div>}
                {linkedinCommentTaskError && <div className="text-sm text-red-400">{linkedinCommentTaskError}</div>}
              </div>
            </div>

            {generationProfiles.length > 0 && (
              <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Discovered Profiles</div>
                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-400">
                      <tr>
                        <th className="px-2 py-2">Source</th>
                        <th className="px-2 py-2">Name</th>
                        <th className="px-2 py-2">Company</th>
                        <th className="px-2 py-2">Profile</th>
                        <th className="px-2 py-2">Post</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generationProfiles.map((profile, index) => (
                        <tr key={`${profile.linkedinUrl}-${index}`} className="border-t border-gray-800">
                          <td className="px-2 py-2 align-top text-white">{profile.sourceType || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{profile.name || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{profile.company || '-'}</td>
                          <td className="px-2 py-2 align-top break-all">
                            <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                              {profile.linkedinUrl}
                            </a>
                          </td>
                          <td className="px-2 py-2 align-top break-all">
                            {profile.postUrl ? (
                              <a href={profile.postUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                                {profile.postUrl}
                              </a>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {generationOutcomes.length > 0 && (
              <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Research Outcomes</div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/30 p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wide text-gray-400">Search Miss Reasons</div>
                  {linkedinUnmatchedReasonSummary.length === 0 ? (
                    <div className="text-xs text-green-300">All discovered profiles returned a search result.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {linkedinUnmatchedReasonSummary.map((item) => (
                        <span key={item.reason} className="inline-flex items-center gap-2 rounded-full border border-gray-700 bg-gray-900 px-3 py-1 text-xs text-gray-200">
                          <span>{item.reason}</span>
                          <span className="font-semibold text-white">{item.count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {linkedinResearchFailureCount > 0 && (
                    <div className="text-xs text-amber-300">
                      Search returned but not researched: {linkedinResearchFailureCount}
                    </div>
                  )}
                </div>
                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-400">
                      <tr>
                        <th className="px-2 py-2">Profile</th>
                        <th className="px-2 py-2">Search Found</th>
                        <th className="px-2 py-2">Researched</th>
                        <th className="px-2 py-2">Contact ID</th>
                        <th className="px-2 py-2">Company ID</th>
                        <th className="px-2 py-2">Outcome</th>
                        <th className="px-2 py-2">Search Result</th>
                        <th className="px-2 py-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generationOutcomes.map((outcome) => (
                        <tr key={outcome.profileUrl} className="border-t border-gray-800">
                          <td className="px-2 py-2 align-top break-all">
                            <a href={outcome.profileUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                              {outcome.name || outcome.profileUrl}
                            </a>
                            <div className="text-gray-500">{outcome.company || '-'}</div>
                          </td>
                          <td className="px-2 py-2 align-top text-white">{outcome.searchResultId ? 'yes' : 'no'}</td>
                          <td className="px-2 py-2 align-top text-white">{outcome.researched ? 'yes' : 'no'}</td>
                          <td className="px-2 py-2 align-top text-gray-300">{outcome.contactId || '-'}</td>
                          <td className="px-2 py-2 align-top text-gray-300">{outcome.companyEntityId || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{outcome.writeDisposition || '-'}</td>
                          <td className="px-2 py-2 align-top text-gray-300">{outcome.searchResultId || '-'}</td>
                          <td className="px-2 py-2 align-top text-red-300">{outcome.error || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {activeTab === 'facebook_generation' && (
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Facebook Generation</h2>
              <p className="text-gray-400 text-sm mt-1">
                One click workflow: Facebook post search → scrape authors and commenters from selected posts → create or enrich contacts from scraped Facebook data → queue `FACEBOOK_COMMENTER`.
              </p>
            </div>

            <div className="space-y-3">
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Keyword / Query</div>
                <input
                  value={facebookKeyword}
                  onChange={(e) => setFacebookKeyword(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="lead generation"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Specific Facebook Post URLs</div>
                <textarea
                  value={facebookSpecificPostUrlsText}
                  onChange={(e) => setFacebookSpecificPostUrlsText(e.target.value)}
                  className="w-full min-h-[110px] bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder={'Optional. Paste one Facebook post URL per line to scrape specific links directly.\nhttps://www.facebook.com/.../posts/...\nhttps://www.facebook.com/.../posts/...'}
                />
                <div className="text-xs text-gray-400 mt-2">
                  If specific post URLs are provided, Facebook Generation will scrape those links directly instead of relying on keyword discovery.
                </div>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Posts To Process</div>
                  <select
                    value={facebookPostCount}
                    onChange={(e) => setFacebookPostCount(Number(e.target.value))}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {LINKEDIN_POST_COUNT_OPTIONS.map((option) => (
                      <option key={`fb-${option.requested}`} value={option.requested}>
                        {option.requested}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Date Range</div>
                  <select
                    value={facebookDateFilter}
                    onChange={(e) => setFacebookDateFilter(e.target.value)}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {LINKEDIN_DATE_OPTIONS.map((option) => (
                      <option key={`fb-${option.value || 'any'}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {canUsePepaiResearch && instantlyCampaigns.length > 0 ? (
                  <label className="block">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Email Campaign</div>
                    <select
                      value={selectedInstantlyCampaignId}
                      onChange={(e) => setSelectedInstantlyCampaignId(e.target.value)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {instantlyCampaigns.map((campaign) => (
                        <option key={`fb-campaign-${campaign.id}`} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={runFacebookGeneration}
                  disabled={facebookGenerationLoading || !userEmail}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {facebookGenerationLoading ? 'Running…' : facebookSpecificPostUrls.length > 0 ? 'Run Facebook Link Scrape' : 'Run Facebook Generation'}
                </button>
                <button
                  onClick={() => void scheduleOneClickRun('facebook')}
                  disabled={oneClickScheduleLoading.facebook || !userEmail}
                  className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {oneClickScheduleLoading.facebook ? 'Scheduling…' : 'Schedule Facebook Run'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Posts Found</div>
                <div className="text-white font-semibold">{facebookGenerationStats.postsFound}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Posts Processed</div>
                <div className="text-white font-semibold">{facebookGenerationStats.postsProcessed}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Author Profiles</div>
                <div className="text-white font-semibold">{facebookGenerationStats.authorProfilesFound}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Profiles Created</div>
                <div className="text-white font-semibold">{facebookGenerationStats.profilesSearched}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Contacts Saved</div>
                <div className="text-white font-semibold">{facebookGenerationStats.researched}</div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2">
                <div className="text-gray-400 text-xs">Sequences</div>
                <div className="text-white font-semibold">{facebookGenerationStats.sequencesQueued}</div>
              </div>
            </div>

            {facebookGenerationStatus && <div className="text-sm text-sky-300">{facebookGenerationStatus}</div>}
            {facebookGenerationLoading && facebookGenerationStats.postsFound > 0 && (
              <div className="text-xs text-gray-400">Current post: {facebookGenerationCurrentPost}/{facebookGenerationStats.postsFound}</div>
            )}
            {facebookGenerationError && <div className="text-sm text-red-400">{facebookGenerationError}</div>}

            {facebookPosts.length > 0 && (
              <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Selected Posts</div>
                <div className="overflow-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-400">
                      <tr>
                        <th className="px-2 py-2">Post</th>
                        <th className="px-2 py-2">Comments</th>
                        <th className="px-2 py-2">Author</th>
                        <th className="px-2 py-2">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facebookPosts.map((post) => (
                        <tr key={post.postUrl} className="border-t border-gray-800">
                          <td className="px-2 py-2 align-top break-all">
                            <a href={post.postUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                              {post.postUrl}
                            </a>
                          </td>
                          <td className="px-2 py-2 align-top text-white">{post.commentCount}</td>
                          <td className="px-2 py-2 align-top text-white">{post.authorName || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{post.authorCompany || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-4">
              <div>
                <div className="text-sm font-semibold text-white">Facebook Comment Tasks</div>
                <p className="text-xs text-amber-300 mt-1">
                  Comment actions are always executed via proxy. In many cases completion takes 2-3 hours. This pairs well with your branded social organic posting.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <label className="block md:col-span-2">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Mode</div>
                  <select
                    value={facebookCommentTaskMode}
                    onChange={(e) => {
                      setFacebookCommentTaskMode(e.target.value as FacebookCommentTaskMode);
                      setFacebookCommentTaskStatus('');
                      setFacebookCommentTaskError('');
                    }}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {FACEBOOK_COMMENT_TASK_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-400 mt-2">
                    {FACEBOOK_COMMENT_TASK_OPTIONS.find((option) => option.value === facebookCommentTaskMode)?.help}
                  </div>
                </label>
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Delay (seconds)</div>
                  <input
                    type="number"
                    min={5}
                    value={facebookCommentDelaySeconds}
                    onChange={(e) => setFacebookCommentDelaySeconds(Math.max(5, Number(e.target.value) || 5))}
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                  />
                </label>
              </div>

              {(facebookCommentTaskMode === 'specific_post_commenter' || facebookCommentTaskMode === 'group_bulk_comment') && (
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                    {facebookCommentTaskMode === 'specific_post_commenter' ? 'Shared Comment Fallback' : 'Bulk Comment'}
                  </div>
                  <textarea
                    value={facebookSharedComment}
                    onChange={(e) => setFacebookSharedComment(e.target.value)}
                    className="w-full min-h-[110px] bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder={
                      facebookCommentTaskMode === 'specific_post_commenter'
                        ? 'Optional shared comment to use unless a post has its own custom comment'
                        : 'Comment to post on every Facebook group URL'
                    }
                  />
                </label>
              )}

              {(facebookCommentTaskMode === 'group_bulk_comment' || facebookCommentTaskMode === 'group_custom_comment') && (
                <label className="block">
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Facebook Group URLs</div>
                  <textarea
                    value={facebookGroupUrlsText}
                    onChange={(e) => setFacebookGroupUrlsText(e.target.value)}
                    className="w-full min-h-[110px] bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder={'https://www.facebook.com/groups/2359489277678976\nhttps://www.facebook.com/groups/1234567890'}
                  />
                </label>
              )}

              {facebookCommentTaskMode === 'specific_post_commenter' &&
                (facebookPosts.length > 0 || facebookSpecificPostUrls.length > 0) && (
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wide text-gray-400">Per-Post Comments</div>
                  <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                    {(facebookPosts.length > 0
                      ? facebookPosts
                      : facebookSpecificPostUrls.map((postUrl) => ({
                          postUrl,
                          commentCount: 0,
                          authorFacebookUrl: '',
                          authorName: '',
                          authorCompany: '',
                        }))).map((post) => (
                      <div key={`comment-post-${post.postUrl}`} className="rounded-lg border border-gray-800 bg-gray-900/30 p-3 space-y-2">
                        <div className="text-xs text-sky-300 break-all">{post.postUrl}</div>
                        <textarea
                          value={facebookCommentDrafts[post.postUrl] ?? ''}
                          onChange={(e) => setFacebookCommentDraft(post.postUrl, e.target.value)}
                          className="w-full min-h-[84px] bg-gray-950/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                          placeholder="Optional custom comment for this post"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {facebookCommentTaskMode === 'group_custom_comment' && facebookGroupUrls.length > 0 && (
                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wide text-gray-400">Per-Group Comments</div>
                  <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                    {facebookGroupUrls.map((groupUrl) => (
                      <div key={`comment-group-${groupUrl}`} className="rounded-lg border border-gray-800 bg-gray-900/30 p-3 space-y-2">
                        <div className="text-xs text-sky-300 break-all">{groupUrl}</div>
                        <textarea
                          value={facebookCommentDrafts[groupUrl] ?? ''}
                          onChange={(e) => setFacebookCommentDraft(groupUrl, e.target.value)}
                          className="w-full min-h-[84px] bg-gray-950/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                          placeholder="Comment for this Facebook group URL"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={createFacebookCommentTasks}
                  disabled={
                    facebookCommentTaskLoading ||
                    !userEmail ||
                    (facebookCommentTaskMode === 'specific_post_commenter'
                      ? facebookPosts.length === 0 && facebookSpecificPostUrls.length === 0
                      : facebookGroupUrls.length === 0)
                  }
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {facebookCommentTaskLoading ? 'Creating Tasks…' : 'Create Comment Tasks'}
                </button>
                {facebookCommentTaskStatus && <div className="text-sm text-green-300">{facebookCommentTaskStatus}</div>}
                {facebookCommentTaskError && <div className="text-sm text-red-400">{facebookCommentTaskError}</div>}
              </div>
            </div>

            {facebookProfiles.length > 0 && (
              <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Discovered Profiles</div>
                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-400">
                      <tr>
                        <th className="px-2 py-2">Source</th>
                        <th className="px-2 py-2">Name</th>
                        <th className="px-2 py-2">Company</th>
                        <th className="px-2 py-2">Email</th>
                        <th className="px-2 py-2">Profile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facebookProfiles.map((profile, index) => (
                        <tr key={`${profile.facebookUrl}-${index}`} className="border-t border-gray-800">
                          <td className="px-2 py-2 align-top text-white">{profile.sourceType || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{profile.name || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{profile.company || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{profile.email || '-'}</td>
                          <td className="px-2 py-2 align-top break-all">
                            <a href={profile.facebookUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                              {profile.facebookUrl}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {facebookOutcomes.length > 0 && (
              <div className="rounded-xl border border-border-dark bg-card-dark p-4 space-y-3">
                <div className="text-sm font-semibold text-white">Research Outcomes</div>
                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-400">
                      <tr>
                        <th className="px-2 py-2">Profile</th>
                        <th className="px-2 py-2">Matched</th>
                        <th className="px-2 py-2">Researched</th>
                        <th className="px-2 py-2">Contact ID</th>
                        <th className="px-2 py-2">Company ID</th>
                        <th className="px-2 py-2">Outcome</th>
                        <th className="px-2 py-2">Search Result</th>
                        <th className="px-2 py-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facebookOutcomes.map((outcome) => (
                        <tr key={outcome.profileUrl} className="border-t border-gray-800">
                          <td className="px-2 py-2 align-top break-all">
                            <a href={outcome.profileUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                              {outcome.name || outcome.profileUrl}
                            </a>
                            <div className="text-gray-500">{outcome.company || '-'}</div>
                          </td>
                          <td className="px-2 py-2 align-top text-white">{outcome.matched ? 'yes' : 'no'}</td>
                          <td className="px-2 py-2 align-top text-white">{outcome.researched ? 'yes' : 'no'}</td>
                          <td className="px-2 py-2 align-top text-gray-300">{outcome.contactId || '-'}</td>
                          <td className="px-2 py-2 align-top text-gray-300">{outcome.companyEntityId || '-'}</td>
                          <td className="px-2 py-2 align-top text-white">{outcome.writeDisposition || '-'}</td>
                          <td className="px-2 py-2 align-top text-gray-300">{outcome.searchResultId || '-'}</td>
                          <td className="px-2 py-2 align-top text-red-300">{outcome.error || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {contactProfileDrawer && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setContactProfileDrawer(null)} />
            <div className="fixed top-0 right-0 w-[460px] max-w-[95vw] h-full bg-gray-900 border-l border-gray-700 z-50 overflow-y-auto">
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-white">{getContactName(contactProfileDrawer.contact) || 'Contact'}</h3>
                    <p className="text-sm text-gray-400">{String(contactProfileDrawer.contact.title ?? contactProfileDrawer.contact.Title ?? '').trim() || 'No title'}</p>
                    <p className="text-xs text-gray-500">{getContactCompany(contactProfileDrawer.contact) || '-'}</p>
                  </div>
                  <button onClick={() => setContactProfileDrawer(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
                </div>

                <div className="rounded-lg border border-gray-700 bg-gray-900/30 p-3 space-y-2">
                  {(['linkedin', 'facebook', 'instagram', 'tiktok', 'x', 'substack'] as OneClickChannel[]).map((channel) => {
                    const url = getContactSocialUrl(contactProfileDrawer.contact, channel);
                    const platform = channelToSocialPlatform(channel);
                    return (
                      <div key={`profile-channel-${channel}`} className="flex items-center justify-between gap-2 border-b border-gray-800 last:border-b-0 py-1">
                        <div className="text-sm text-gray-300">{channel === 'x' ? 'X/Twitter' : channel}</div>
                        <div className="flex items-center gap-2">
                          {url ? (
                            <a href={url} target="_blank" rel="noreferrer" className="text-xs text-sky-400 hover:underline">
                              Open
                            </a>
                          ) : (
                            <span className="text-xs text-gray-500">missing</span>
                          )}
                          {platform ? (
                            <button
                              onClick={() => setContactSocialDrawer({ contact: contactProfileDrawer.contact, platform })}
                              className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs"
                            >
                              Drawer
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {contactSocialDrawer && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setContactSocialDrawer(null)} />
            <div className="fixed top-0 right-0 w-[420px] max-w-[95vw] h-full bg-gray-900 border-l border-gray-700 z-50 overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-white">{getContactName(contactSocialDrawer.contact) || 'Contact'}</h3>
                    <p className="text-sm text-gray-400">{contactSocialDrawer.platform} Profile</p>
                  </div>
                  <button onClick={() => setContactSocialDrawer(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  {(() => {
                    const url = getContactSocialUrl(contactSocialDrawer.contact, contactSocialDrawer.platform);
                    if (url) {
                      return (
                        <a href={url} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 underline break-all">
                          {url}
                        </a>
                      );
                    }
                    return (
                      <div className="text-sm text-gray-400">
                        Add a URL or run Find to locate this profile.
                      </div>
                    );
                  })()}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {(() => {
                    const url = getContactSocialUrl(contactSocialDrawer.contact, contactSocialDrawer.platform);
                    const ids = getContactSocialDatasetIds(contactSocialDrawer.contact, contactSocialDrawer.platform);
                    const searchResultId = String(contactSocialDrawer.contact.searchResultId ?? '').trim();
                    const key = `${searchResultId}-${contactSocialDrawer.platform}`;
                    return (
                      <>
                        {(ids.profile || ids.posts) ? (
                          <button
                            onClick={() => handleViewContactSocialReport(contactSocialDrawer.contact, contactSocialDrawer.platform)}
                            disabled={!!contactSocialReportLoading[key]}
                            className="px-3 py-2 rounded text-[#156BFF] text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                          >
                            {contactSocialReportLoading[key] ? 'Loading...' : 'View Report'}
                          </button>
                        ) : null}
                        {url ? (
                          <button
                            onClick={() => window.open(url, '_blank')}
                            className="px-3 py-2 rounded text-white text-sm bg-gray-700 hover:bg-gray-600"
                          >
                            Open
                          </button>
                        ) : null}
                        {searchResultId ? (
                          <>
                            <button
                              onClick={() => handleScrapeContactSocial(contactSocialDrawer.contact, contactSocialDrawer.platform)}
                              disabled={!!socialScrapeLoading[key]}
                              className="px-3 py-2 rounded text-white text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                            >
                              {socialScrapeLoading[key] ? 'Scraping...' : 'Scrape'}
                            </button>
                            <button
                              onClick={() => handleFindContactSocial(contactSocialDrawer.contact, contactSocialDrawer.platform)}
                              disabled={!!socialLoading[key]}
                              className="px-3 py-2 rounded text-white text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                            >
                              {socialLoading[key] ? 'Finding...' : 'Find'}
                            </button>
                          </>
                        ) : null}
                        <button
                          onClick={() => {
                            setSocialEdit(contactSocialDrawer);
                            setSocialEditValue(url || '');
                          }}
                          className="px-3 py-2 rounded text-white text-sm bg-gray-800 hover:bg-gray-700"
                        >
                          Edit
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {socialEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/80" onClick={() => { setSocialEdit(null); setSocialEditValue(''); }} />
            <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
              <h3 className="text-xl font-bold text-white mb-2">Set {socialEdit.platform} URL</h3>
              <p className="text-sm text-gray-400 mb-4">{getContactName(socialEdit.contact) || 'Contact'}</p>
              <input
                type="text"
                value={socialEditValue}
                onChange={(e) => setSocialEditValue(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setSocialEdit(null); setSocialEditValue(''); }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveContactSocial}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold"
                >
                  Save URL
                </button>
              </div>
            </div>
          </div>
        )}

        {contactSocialReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/80" onClick={() => setContactSocialReport(null)} />
            <div className="bg-card-dark border border-gray-700 rounded-xl max-w-3xl w-full mx-4 p-6 relative z-10 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">{contactSocialReport.platform} Report</h3>
                  <p className="text-sm text-gray-400">{getContactName(contactSocialReport.contact) || 'Contact'}</p>
                </div>
                <button onClick={() => setContactSocialReport(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
              </div>
              <pre className="text-xs text-gray-200 whitespace-pre-wrap break-words bg-gray-900/60 border border-gray-800 rounded-lg p-3">
                {JSON.stringify(contactSocialReport.items, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
